// src/components/ExecutiveIntelligencePage.jsx
// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useAdvisor } from "../AdvisorContext";
import { ActionGroup, Button, Select } from "../design-system/components";
import ExecutiveIntelligencePanel from "./executive/ExecutiveIntelligencePanel";
import ContextualExplainDrawer from "./ai/ContextualExplainDrawer";
import "../dashboard.css";



function normalizeFrameworkKey(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
}

function canonicalFrameworkLabel(value) {
  const key = normalizeFrameworkKey(value);
  const labels = {
    AMLKYC: "AML/KYC",
    CCPA: "CCPA",
    FDA: "FDA",
    FEDRAMP: "FedRAMP",
    HIPAA: "HIPAA",
    ISO: "ISO",
    ISO27001: "ISO 27001",
    NIST: "NIST",
    OSHA: "OSHA",
    PCIDSS: "PCI DSS",
    SOC2: "SOC2",
  };

  return labels[key] || String(value || "Unknown").trim() || "Unknown";
}

function deduplicateFrameworkRows(rows = []) {
  const byFramework = new Map();

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const key = normalizeFrameworkKey(row?.framework);
    if (!key) return;

    const current = byFramework.get(key);
    const candidateTime = new Date(row?.latest_at || row?.captured_at || 0).getTime();
    const currentTime = new Date(current?.latest_at || current?.captured_at || 0).getTime();

    if (!current || candidateTime >= currentTime) {
      byFramework.set(key, {
        ...row,
        framework: canonicalFrameworkLabel(row?.framework),
      });
    }
  });

  return Array.from(byFramework.values()).sort((a, b) =>
    String(a.framework).localeCompare(String(b.framework))
  );
}

const WINDOW_OPTIONS = [
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
  { value: "1y", label: "1 year" },
];

const REQUEST_TIMEOUT_MS = 30000;
const EXPORT_NOTICE_TIMEOUT_MS = 7000;

const EXECUTIVE_EXPLAIN_PROMPTS = [
  "Why is this recommended?",
  "What is driving this?",
  "What changed underneath this?",
  "What should leadership review next?",
];

function safeNumber(value, fallback = 0) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
}

function clampPercent(value) {
  return Math.max(0, Math.min(100, safeNumber(value, 0)));
}

function formatPercent(value, digits = 1) {
  const numeric = safeNumber(value, null);
  return numeric === null ? "—" : `${numeric.toFixed(digits)}%`;
}

function formatNumber(value, digits = 0) {
  const numeric = safeNumber(value, null);
  if (numeric === null) return "—";
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function formatDays(value) {
  const numeric = safeNumber(value, null);
  return numeric === null ? "—" : `${numeric.toFixed(2)}d`;
}

function formatSigned(value, digits = 2) {
  const numeric = safeNumber(value, null);
  if (numeric === null) return "—";
  return numeric > 0 ? `+${numeric.toFixed(digits)}` : numeric.toFixed(digits);
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatShortDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatDurationSeconds(value) {
  const numeric = safeNumber(value, null);
  if (numeric === null) return "0s";
  if (numeric < 60) return `${Math.round(numeric)}s`;
  const minutes = numeric / 60;
  if (minutes < 60) return `${minutes.toFixed(minutes >= 10 ? 0 : 1)}m`;
  const hours = minutes / 60;
  if (hours < 24) return `${hours.toFixed(hours >= 10 ? 0 : 1)}h`;
  const days = hours / 24;
  return `${days.toFixed(days >= 10 ? 0 : 1)}d`;
}

function formatSourceLabel(value) {
  const raw = String(value || "").trim();
  if (!raw) return "Live Analytics";

  const knownLabels = {
    current_score_snapshots: "Current Score Snapshots",
    framework_score_snapshots: "Framework Score Snapshots",
    live_analytics: "Live Analytics",
    score_snapshots: "Score Snapshots",
  };

  if (knownLabels[raw]) return knownLabels[raw];

  return raw
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1));
}

function buildDownloadFilename({ framework = "organization", windowValue = "30d" } = {}) {
  const date = new Date().toISOString().slice(0, 10);
  const safeFramework = String(framework || "organization")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

  return `executive-intelligence-${safeFramework || "organization"}-${windowValue}-${date}.json`;
}

function buildPdfDownloadFilename({ framework = "organization", windowValue = "30d" } = {}) {
  const date = new Date().toISOString().slice(0, 10);
  const safeFramework = String(framework || "organization")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

  return `oasys-executive-report-${safeFramework || "organization"}-${windowValue}-${date}.pdf`;
}

function getToneForScore(value) {
  const score = safeNumber(value);
  if (score >= 90) return "ok";
  if (score >= 75) return "warn";
  return "danger";
}

function getToneStyle(tone = "default") {
  if (tone === "ok") return { color: "var(--ok)" };
  if (tone === "warn") return { color: "var(--warn)" };
  if (tone === "danger") return { color: "var(--danger)" };
  if (tone === "accent") return { color: "var(--brand)" };
  return { color: "#E8EEF8" };
}

function getRiskLabel(score) {
  const numeric = safeNumber(score);
  if (numeric >= 90) return { label: "Stable", className: "risk-low", tone: "ok" };
  if (numeric >= 75) return { label: "Watchlist", className: "risk-medium", tone: "warn" };
  return { label: "Action Required", className: "risk-high", tone: "danger" };
}

function buildTrendSummary(postureTrend = []) {
  if (!postureTrend.length) {
    return {
      current: null,
      previous: null,
      movement: null,
      label: "No historical posture data available yet.",
      tone: "default",
    };
  }

  const first = postureTrend[0];
  const last = postureTrend[postureTrend.length - 1];
  const firstScore = safeNumber(
    first?.operational_score ?? first?.latest_score ?? first?.raw_score,
    null
  );
  const lastScore = safeNumber(
    last?.operational_score ?? last?.latest_score ?? last?.raw_score,
    null
  );

  if (firstScore === null || lastScore === null) {
    return {
      current: null,
      previous: null,
      movement: null,
      label: "Posture trend is available but score values are incomplete.",
      tone: "default",
    };
  }

  const movement = lastScore - firstScore;
  const tone = movement > 0 ? "ok" : movement < 0 ? "danger" : "default";
  const direction =
    movement > 0 ? "improved" : movement < 0 ? "declined" : "held steady";

  return {
    current: lastScore,
    previous: firstScore,
    movement,
    label: `Posture ${direction} ${formatSigned(movement)} points across the selected window.`,
    tone,
  };
}

function compressPostureTrendByDay(rows = []) {
  const dailyLatest = new Map();

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const rawDate = row?.captured_at || row?.bucket || row?.created_at;
    if (!rawDate) return;

    const date = new Date(rawDate);
    if (Number.isNaN(date.getTime())) return;

    const dayKey = date.toISOString().slice(0, 10);
    const previous = dailyLatest.get(dayKey);
    const previousDate = previous ? new Date(previous.captured_at || previous.bucket || 0) : null;

    if (!previous || date >= previousDate) {
      dailyLatest.set(dayKey, row);
    }
  });

  const compressed = Array.from(dailyLatest.values()).sort((a, b) => {
    const aTime = new Date(a?.captured_at || a?.bucket || 0).getTime();
    const bTime = new Date(b?.captured_at || b?.bucket || 0).getTime();
    return aTime - bTime;
  });

  return compressed.length ? compressed : rows;
}

function buildExecutiveSummaryCopy(summaryText, validationMetrics = {}) {
  const fallback = "Executive summary unavailable for the selected scope.";
  const text = String(summaryText || "").trim();

  if (!text) return fallback;
  if (safeNumber(validationMetrics?.runs) > 0) return text;

  return text.replace(
    /validation acceptance is [^;]+;/i,
    "no validation runs were recorded;"
  );
}

function buildValidationExecutiveMetrics(validation = {}) {
  const runs = safeNumber(validation?.validation_runs);
  const passed = safeNumber(validation?.passed);
  const failed = safeNumber(validation?.failed);
  const manualOverrides = safeNumber(validation?.manual_overrides);
  const approvedExceptions = safeNumber(validation?.approved_exceptions);

  const acceptedOutcomes = passed + approvedExceptions;
  const governanceBurden = manualOverrides + approvedExceptions;

  return {
    runs,
    passed,
    failed,
    manualOverrides,
    approvedExceptions,
    acceptedOutcomes,
    governanceBurden,
    acceptanceRate: runs > 0 ? (acceptedOutcomes / runs) * 100 : 0,
    failureRate: runs > 0 ? (failed / runs) * 100 : 0,
    governanceBurdenRate: runs > 0 ? (governanceBurden / runs) * 100 : 0,
  };
}

function buildExecutiveDriver({ riskVelocity = {}, throughput = {}, validationMetrics = {}, trendSummary = {} } = {}) {
  const netVelocity = safeNumber(riskVelocity?.net);
  const tasksClosed = safeNumber(throughput?.tasks_closed);
  const tasksOpened = safeNumber(throughput?.tasks_opened);
  const movement = safeNumber(trendSummary?.movement, 0);

  if (netVelocity < 0) {
    return {
      label: "Risk reduction is outpacing new findings",
      detail: `${formatNumber(riskVelocity.resolved)} findings were resolved while ${formatNumber(riskVelocity.new)} new findings were recorded in the selected window.`,
      tone: movement < 0 ? "warn" : "ok",
    };
  }

  if (movement > 0 && validationMetrics.acceptedOutcomes > 0) {
    return {
      label: "Validated and accepted outcomes are supporting posture",
      detail: `${formatNumber(validationMetrics.acceptedOutcomes)} accepted validation outcomes were recorded, including ${formatNumber(validationMetrics.approvedExceptions)} approved exceptions.`,
      tone: "ok",
    };
  }

  if (netVelocity > 0) {
    return {
      label: "New risk is outpacing reduction",
      detail: `${formatNumber(riskVelocity.new)} new findings were recorded while ${formatNumber(riskVelocity.resolved)} findings were resolved in the selected window.`,
      tone: "danger",
    };
  }

  if (movement < 0) {
    return {
      label: "Posture declined in the selected window",
      detail: `Operational posture moved ${formatSigned(movement)} points. Leadership should review the underlying framework and control changes.`,
      tone: "danger",
    };
  }

  if (tasksOpened > tasksClosed) {
    return {
      label: "Workload intake exceeds closure",
      detail: `${formatNumber(tasksOpened)} tasks opened while ${formatNumber(tasksClosed)} closed in the selected window.`,
      tone: "warn",
    };
  }

  return {
    label: "Posture is stable",
    detail: "No material deterioration signal is visible in the selected window.",
    tone: "default",
  };
}

function ExecutiveMetricCard({ label, value, footnote, tone = "default" }) {
  return (
    <div className="hero-kpi" style={{ color: "#E8EEF8" }}>
      <div className="kpi-label" style={{ color: "#E8EEF8" }}>
        {label}
      </div>
      <div className="kpi-value small" style={getToneStyle(tone)}>
        {value}
      </div>
      {footnote ? (
        <div className="hero-kpi-foot" style={{ color: "#A8B3C7" }}>
          {footnote}
        </div>
      ) : null}
    </div>
  );
}

function InsightCard({ eyebrow, title, detail, tone = "default" }) {
  const borderColor =
    tone === "ok"
      ? "rgba(74, 222, 128, 0.35)"
      : tone === "danger"
      ? "rgba(248, 113, 113, 0.35)"
      : tone === "warn"
      ? "rgba(251, 191, 36, 0.35)"
      : "rgba(96, 165, 250, 0.3)";

  return (
    <section
      className="card"
      style={{
        padding: 18,
        borderColor,
        background:
          tone === "ok"
            ? "linear-gradient(135deg, rgba(16,185,129,.13), rgba(15,23,42,.62))"
            : tone === "danger"
            ? "linear-gradient(135deg, rgba(239,68,68,.12), rgba(15,23,42,.62))"
            : tone === "warn"
            ? "linear-gradient(135deg, rgba(245,158,11,.12), rgba(15,23,42,.62))"
            : "linear-gradient(135deg, rgba(96,165,250,.11), rgba(15,23,42,.62))",
      }}
    >
      <div className="dash-eyebrow" style={{ marginBottom: 8 }}>
        {eyebrow}
      </div>
      <div style={{ color: "#f8fafc", fontWeight: 900, fontSize: 17, lineHeight: 1.2 }}>
        {title}
      </div>
      <div style={{ color: "#A8B3C7", fontSize: 13, lineHeight: 1.55, marginTop: 8 }}>
        {detail}
      </div>
    </section>
  );
}

function getActionPriorityStyle(priority = "normal") {
  const normalized = String(priority || "normal").toLowerCase();

  if (normalized === "critical") {
    return {
      color: "#fecaca",
      background: "rgba(239,68,68,.16)",
      borderColor: "rgba(248,113,113,.42)",
    };
  }

  if (normalized === "high") {
    return {
      color: "#fecaca",
      background: "rgba(239,68,68,.12)",
      borderColor: "rgba(248,113,113,.35)",
    };
  }

  if (normalized === "medium") {
    return {
      color: "#fde68a",
      background: "rgba(245,158,11,.12)",
      borderColor: "rgba(251,191,36,.34)",
    };
  }

  return {
    color: "#bbf7d0",
    background: "rgba(16,185,129,.10)",
    borderColor: "rgba(74,222,128,.28)",
  };
}

function LeadershipActionBriefing({ actions = [], onOpenAction, onExplainAction }) {
  const visibleActions = Array.isArray(actions) ? actions : [];

  return (
    <section className="section-card" style={{ marginTop: 16 }}>
      <div className="section-header">
        <div>
          <div className="section-title">Leadership Action Briefing</div>
          <div className="section-subtitle">
            Prioritized executive actions generated from current posture, SLA, validation, and remediation movement.
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div className="kpi-label">Actions</div>
          <div style={{ color: "#E8EEF8", fontWeight: 900 }}>
            {formatNumber(visibleActions.length)}
          </div>
        </div>
      </div>

      <div
        className="card-body"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 12,
        }}
      >
        {visibleActions.length === 0 ? (
          <div className="muted">No leadership actions are available for the selected window.</div>
        ) : (
          visibleActions.map((action, index) => {
            const priority = String(action?.priority || "normal").toUpperCase();
            const priorityStyle = getActionPriorityStyle(action?.priority);

            return (
              <article
                key={action?.key || `${action?.title || "action"}-${index}`}
                style={{
                  display: "grid",
                  alignContent: "space-between",
                  gap: 14,
                  border: `1px solid ${priorityStyle.borderColor}`,
                  borderRadius: 16,
                  padding: 16,
                  background: "rgba(15,23,42,.34)",
                }}
              >
                <div style={{ display: "grid", gap: 10 }}>
                  <span
                    style={{
                      justifySelf: "start",
                      padding: "4px 10px",
                      borderRadius: 999,
                      border: `1px solid ${priorityStyle.borderColor}`,
                      background: priorityStyle.background,
                      color: priorityStyle.color,
                      fontSize: 11,
                      fontWeight: 900,
                      letterSpacing: ".05em",
                    }}
                  >
                    {priority}
                  </span>
                  <div style={{ color: "#f8fafc", fontWeight: 900, fontSize: 16, lineHeight: 1.3 }}>
                    {action?.title || "Leadership action"}
                  </div>
                  <div style={{ color: "#A8B3C7", fontSize: 13, lineHeight: 1.55 }}>
                    {action?.detail || "Review the selected compliance scope."}
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                    justifySelf: "start",
                  }}
                >
                  {action?.action_label ? (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => onOpenAction?.(action)}
                    >
                      {action.action_label}
                    </button>
                  ) : null}

                  <Button
                    type="button"
                    variant="ghost"
                    size="small"
                    onClick={() => onExplainAction?.(action, index)}
                  >
                    Explain
                  </Button>
                </div>
              </article>
            );
          })
        )}
      </div>
    </section>
  );
}

function HealthDonut({ value }) {
  const pct = clampPercent(value);
  const risk = getRiskLabel(pct);
  const tone =
    risk.tone === "ok"
      ? "success"
      : risk.tone === "warn"
      ? "warning"
      : "danger";
  const ringDegrees = Math.max(0, Math.min(360, pct * 3.6));

  return (
    <div className="executive-intelligence-score">
      <div
        className={`executive-intelligence-score__ring executive-intelligence-score__ring--${tone}`}
        style={{ "--executive-score-degrees": `${ringDegrees}deg` }}
        title={`Executive health score ${pct.toFixed(1)}%`}
      >
        <div className="executive-intelligence-score__center">
          <span>Operational score</span>
          <strong>{formatPercent(pct)}</strong>
          <span className={`executive-intelligence-score__status executive-intelligence-score__status--${tone}`}>
            {risk.label}
          </span>
        </div>
      </div>
    </div>
  );
}

function FrameworkHealthCard({ item }) {
  const tone =
    item.trend === "improving"
      ? "ok"
      : item.trend === "deteriorating"
      ? "danger"
      : "warn";

  return (
    <div
      className="open-item-pill"
      style={{ width: "100%", textAlign: "left", color: "#E8EEF8" }}
    >
      <div style={{ display: "grid", gap: 8, width: "100%" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
          <div className="framework-label" style={{ color: "#E8EEF8", fontWeight: 900 }}>
            {item.framework || "Unknown"}
          </div>
          <div style={{ ...getToneStyle(tone), fontWeight: 900 }}>
            {formatSigned(item.movement)}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            color: "#A8B3C7",
            fontSize: 11,
          }}
        >
          <span>Current: {formatPercent(item.current_score)}</span>
          <span>Previous: {formatPercent(item.previous_score)}</span>
          <span style={{ textTransform: "capitalize" }}>{item.trend || "stable"}</span>
        </div>
      </div>
    </div>
  );
}


function ExecutiveWindowEmptyState({ windowValue, subject }) {
  const windowLabel =
    WINDOW_OPTIONS.find((item) => item.value === windowValue)?.label || windowValue;

  return (
    <div className="executive-intelligence-empty-state">
      <strong>No {subject} recorded in this window</strong>
      <p>
        OASYS found no {subject} observations during the selected {windowLabel} period.
        Choose a longer reporting window to review earlier activity.
      </p>
    </div>
  );
}

function FrameworkMovementSummary({ rows = [], selectedFramework }) {
  const selectedKey = normalizeFrameworkKey(selectedFramework);
  const selected =
    rows.find((row) => normalizeFrameworkKey(row.framework) === selectedKey) ||
    rows[0] ||
    null;
  const improving = rows.filter((row) => row.trend === "improving");
  const declining = rows.filter((row) => row.trend === "deteriorating");
  const stable = rows.filter(
    (row) => row.trend !== "improving" && row.trend !== "deteriorating"
  );

  if (!rows.length) {
    return (
      <div className="executive-intelligence-empty-state executive-intelligence-empty-state--compact">
        <strong>No framework movement available</strong>
        <p>Framework snapshots will appear after posture history has been recorded.</p>
      </div>
    );
  }

  return (
    <div className="executive-framework-summary">
      <div className="executive-framework-summary__selected">
        <span>Selected framework</span>
        <strong>{selected?.framework || canonicalFrameworkLabel(selectedFramework)}</strong>
        <b
          className={`executive-framework-summary__movement executive-framework-summary__movement--${
            selected?.trend === "improving"
              ? "success"
              : selected?.trend === "deteriorating"
              ? "danger"
              : "neutral"
          }`}
        >
          {formatSigned(selected?.movement)}
        </b>
        <small>
          {selected
            ? `${formatPercent(selected.current_score)} current · ${
                selected.trend || "stable"
              }`
            : "No selected framework snapshot"}
        </small>
      </div>

      <dl className="executive-framework-summary__counts">
        <div>
          <dt>Improving</dt>
          <dd>{improving.length}</dd>
        </div>
        <div>
          <dt>Declining</dt>
          <dd>{declining.length}</dd>
        </div>
        <div>
          <dt>Stable</dt>
          <dd>{stable.length}</dd>
        </div>
      </dl>

      <details className="executive-framework-summary__details">
        <summary>Review all {rows.length} frameworks</summary>
        <div className="executive-framework-summary__list">
          {rows.map((item) => (
            <FrameworkHealthCard
              key={normalizeFrameworkKey(item.framework)}
              item={item}
            />
          ))}
        </div>
      </details>
    </div>
  );
}

function SimpleLineChart({
  title,
  subtitle,
  rows,
  valueAccessor,
  labelAccessor,
  tone = "ok",
  valueLabel = "Posture Score %",
  valueFormatter = formatPercent,
  windowValue,
}) {
  const values = rows
    .map((row) => safeNumber(valueAccessor(row), null))
    .filter((value) => value !== null);

  const rawMin = values.length ? Math.min(...values) : 0;
  const rawMax = values.length ? Math.max(...values) : 100;
  const chartMin = Math.max(0, Math.floor((rawMin - 1) / 5) * 5);
  const chartMax = Math.min(100, Math.ceil((rawMax + 1) / 5) * 5);
  const range = Math.max(1, chartMax - chartMin);
  const width = 720;
  const height = 240;
  const padX = 54;
  const padY = 30;
  const plotBottom = height - padY;
  const plotTop = padY;
  const plotHeight = plotBottom - plotTop;

  const points = rows
    .map((row, index) => {
      const raw = safeNumber(valueAccessor(row), null);
      if (raw === null) return null;
      const x =
        rows.length <= 1
          ? width / 2
          : padX + (index / (rows.length - 1)) * (width - padX * 2);
      const y = plotBottom - ((raw - chartMin) / range) * plotHeight;
      return { x, y, raw, row };
    })
    .filter(Boolean);

  const path = points
    .map(
      (point, index) =>
        `${index === 0 ? "M" : "L"} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`
    )
    .join(" ");

  const stroke =
    tone === "danger"
      ? "var(--danger)"
      : tone === "warn"
      ? "var(--warn)"
      : tone === "accent"
      ? "var(--brand)"
      : "var(--ok)";

  const latest = points[points.length - 1];
  const first = points[0];
  const movement =
    first && latest ? safeNumber(latest.raw, 0) - safeNumber(first.raw, 0) : null;

  const yAxisTicks = [1, 0.75, 0.5, 0.25, 0].map((position) => {
    const value = chartMin + range * position;
    const y = plotBottom - position * plotHeight;
    return { value, y };
  });

  return (
    <section className="section-card">
      <div className="section-header">
        <div>
          <div className="section-title">{title}</div>
          <div className="section-subtitle">{subtitle}</div>
        </div>
        {latest ? (
          <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
            <div style={{ textAlign: "right" }}>
              <div className="kpi-label">Latest</div>
              <div style={{ ...getToneStyle(tone), fontWeight: 900 }}>
                {valueFormatter(latest.raw)}
              </div>
            </div>
            {movement !== null ? (
              <div style={{ textAlign: "right" }}>
                <div className="kpi-label">Movement</div>
                <div
                  style={{
                    ...getToneStyle(movement > 0 ? "ok" : movement < 0 ? "danger" : "default"),
                    fontWeight: 900,
                  }}
                >
                  {formatSigned(movement)}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="card-body" style={{ display: "grid", gap: 12 }}>
        {points.length === 0 ? (
          <ExecutiveWindowEmptyState
            windowValue={windowValue}
            subject="posture trend"
          />
        ) : (
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                color: "var(--muted)",
                fontSize: 12,
                gap: 12,
              }}
            >
              <span>{valueLabel}</span>
              <span>Time →</span>
            </div>

            <svg
              viewBox={`0 0 ${width} ${height}`}
              role="img"
              aria-label={`${title}. Y-axis shows ${valueLabel}; X-axis shows time.`}
              style={{
                width: "100%",
                minHeight: 240,
                overflow: "visible",
                borderRadius: 18,
                background:
                  "linear-gradient(180deg, rgba(15,23,42,.38), rgba(15,23,42,.12))",
                border: "1px solid rgba(148,163,184,.14)",
              }}
            >
              {yAxisTicks.map((tick, index) => (
                <g key={`${tick.value}-${index}`}>
                  <line
                    x1={padX}
                    x2={width - padX}
                    y1={tick.y}
                    y2={tick.y}
                    stroke="rgba(148,163,184,.14)"
                    strokeWidth="1"
                  />
                  <text
                    x={padX - 12}
                    y={tick.y + 4}
                    textAnchor="end"
                    fill="rgba(203,213,225,.70)"
                    fontSize="11"
                    fontWeight="700"
                  >
                    {formatPercent(tick.value, 0)}
                  </text>
                </g>
              ))}

              <line
                x1={padX}
                x2={padX}
                y1={plotTop}
                y2={plotBottom}
                stroke="rgba(148,163,184,.18)"
                strokeWidth="1"
              />
              <line
                x1={padX}
                x2={width - padX}
                y1={plotBottom}
                y2={plotBottom}
                stroke="rgba(148,163,184,.18)"
                strokeWidth="1"
              />

              <path
                d={path}
                fill="none"
                stroke={stroke}
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />

              {points.map((point, index) => (
                <g key={`${point.x}-${point.y}-${index}`}>
                  <circle cx={point.x} cy={point.y} r="5" fill={stroke} />
                  <title>{`${labelAccessor(point.row)} · ${valueFormatter(point.raw)}`}</title>
                </g>
              ))}
            </svg>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                color: "var(--muted)",
                fontSize: 12,
                gap: 12,
              }}
            >
              <span>{labelAccessor(points[0].row)}</span>
              <span>{points.length} daily snapshots</span>
              <span>{labelAccessor(points[points.length - 1].row)}</span>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function SegmentedValidationChart({ rows, windowValue }) {
  const totals = rows.reduce(
    (acc, row) => {
      acc.total += safeNumber(row.total);
      acc.passed += safeNumber(row.passed);
      acc.failed += safeNumber(row.failed);
      acc.manual += safeNumber(row.manual_overrides);
      acc.exception += safeNumber(row.approved_exceptions);
      return acc;
    },
    { total: 0, passed: 0, failed: 0, manual: 0, exception: 0 }
  );

  const total = Math.max(1, totals.total);
  const segments = [
    { key: "passed", label: "Passed", value: totals.passed, color: "var(--ok)" },
    { key: "failed", label: "Failed", value: totals.failed, color: "var(--danger)" },
    { key: "manual", label: "Manual Override", value: totals.manual, color: "var(--warn)" },
    {
      key: "exception",
      label: "Approved Exception",
      value: totals.exception,
      color: "var(--brand)",
    },
  ];

  return (
    <section className="section-card">
      <div className="section-header">
        <div>
          <div className="section-title">Validation Outcomes</div>
          <div className="section-subtitle">
            Governance outcome mix across validation runs in the selected window.
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div className="kpi-label">Accepted Outcomes</div>
          <div style={{ color: "#E8EEF8", fontWeight: 900 }}>
            {totals.total > 0
              ? formatPercent(
                  ((totals.passed + totals.exception) / totals.total) * 100
                )
              : "No runs"}
          </div>
          <div style={{ color: "#A8B3C7", fontSize: 11 }}>
            {totals.total > 0
              ? `${formatNumber(
                  totals.passed + totals.exception
                )} of ${formatNumber(totals.total)} runs`
              : "No validation activity in this window"}
          </div>
        </div>
      </div>
      <div className="card-body" style={{ display: "grid", gap: 16 }}>
        {rows.length === 0 ? (
          <ExecutiveWindowEmptyState
            windowValue={windowValue}
            subject="validation activity"
          />
        ) : (
          <>
            <div
              style={{
                height: 18,
                borderRadius: 999,
                overflow: "hidden",
                background: "rgba(148,163,184,.14)",
                display: "flex",
              }}
            >
              {segments.map((segment) => {
                const width = totals.total > 0 ? (segment.value / total) * 100 : 0;
                return (
                  <div
                    key={segment.key}
                    title={`${segment.label}: ${segment.value}`}
                    style={{
                      width: `${Math.max(segment.value > 0 ? 3 : 0, width)}%`,
                      background: segment.color,
                    }}
                  />
                );
              })}
            </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: 10,
          }}
        >
          {segments.map((segment) => (
            <div
              key={segment.key}
              style={{
                border: "1px solid rgba(148,163,184,.16)",
                borderRadius: 14,
                padding: 12,
                background: "rgba(15,23,42,.35)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  color: "#A8B3C7",
                  fontSize: 12,
                }}
              >
                <span
                  style={{
                    width: 9,
                    height: 9,
                    borderRadius: 999,
                    background: segment.color,
                    display: "inline-block",
                  }}
                />
                {segment.label}
              </div>
              <div style={{ color: "#f8fafc", fontSize: 22, fontWeight: 900, marginTop: 6 }}>
                {formatNumber(segment.value)}
              </div>
              <div style={{ color: "#A8B3C7", fontSize: 11 }}>
                {formatPercent((segment.value / total) * 100)} of runs
              </div>
            </div>
          ))}
        </div>

            <div style={{ display: "grid", gap: 10 }}>
              {rows.slice(-6).map((row, index) => (
                <div
                  key={`${row.bucket}-${index}`}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "90px 1fr auto",
                    gap: 12,
                    alignItems: "center",
                    color: "#A8B3C7",
                    fontSize: 12,
                  }}
                >
                  <span>{formatDate(row.bucket)}</span>
                  <div
                    style={{
                      height: 8,
                      borderRadius: 999,
                      background: "rgba(148,163,184,.16)",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${Math.min(
                          100,
                          (safeNumber(row.total) / Math.max(1, totals.total)) * 100
                        )}%`,
                        background: "var(--warn)",
                        borderRadius: 999,
                      }}
                    />
                  </div>
                  <strong style={{ color: "#f8fafc" }}>
                    {formatNumber(row.total)}
                  </strong>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function ThroughputChart({ rows, windowValue }) {
  const maxValue = Math.max(
    1,
    ...rows.map(
      (row) =>
        safeNumber(row.opened) +
        safeNumber(row.closed) +
        safeNumber(row.validated) +
        safeNumber(row.failed_validation)
    )
  );

  return (
    <section className="section-card">
      <div className="section-header">
        <div>
          <div className="section-title">Remediation Throughput</div>
          <div className="section-subtitle">
            Daily openings, closures, validations, and failed-validation movement.
          </div>
        </div>
      </div>
      <div className="card-body" style={{ display: "grid", gap: 12 }}>
        {rows.length === 0 ? (
          <ExecutiveWindowEmptyState
            windowValue={windowValue}
            subject="remediation movement"
          />
        ) : (
          rows.slice(-10).map((row, index) => {
            const opened = safeNumber(row.opened);
            const closed = safeNumber(row.closed);
            const validated = safeNumber(row.validated);
            const failed = safeNumber(row.failed_validation);
            const total = opened + closed + validated + failed;
            const width = Math.max(4, Math.min(100, (total / maxValue) * 100));

            return (
              <div key={`${row.bucket}-${index}`} style={{ display: "grid", gap: 7 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    color: "#A8B3C7",
                    fontSize: 12,
                    gap: 12,
                  }}
                >
                  <span>{formatDate(row.bucket)}</span>
                  <span>
                    {formatNumber(total)} movement · {formatNumber(opened)} opened ·{" "}
                    {formatNumber(closed)} closed
                  </span>
                </div>
                <div
                  style={{
                    height: 12,
                    borderRadius: 999,
                    overflow: "hidden",
                    background: "rgba(148,163,184,.16)",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      width: `${width}%`,
                      height: "100%",
                      borderRadius: 999,
                      background:
                        failed > 0
                          ? "linear-gradient(90deg, var(--brand), var(--danger))"
                          : "linear-gradient(90deg, var(--brand), var(--ok))",
                    }}
                  />
                </div>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}


const EXECUTIVE_IDENTITY_RULE_LABELS = {
  GW_2SV_DISABLED: "Google Workspace 2-Step Verification disabled",
  GW_UNUSED_USER: "Google Workspace unused account",
  GW_INACTIVE_ADMIN: "Google Workspace inactive admin",
  GW_ADMIN_COUNT: "Google Workspace admin exposure",
  GW_SUPER_ADMIN_EXPOSURE: "Google Workspace super admin exposure",
  GW_LOGIN_ACTIVITY_OBSERVED: "Google Workspace login activity observed",
  OKTA_MFA_DISABLED: "Okta MFA disabled",
  OKTA_UNUSED_ACCOUNT: "Okta unused account",
  OKTA_STALE_ADMIN: "Okta stale admin",
  OKTA_ADMIN_EXPOSURE: "Okta admin exposure",
  OKTA_PRIVILEGED_GROUP_EXPOSURE: "Okta privileged group exposure",
  OKTA_SYSTEM_LOG_ACTIVITY_OBSERVED: "Okta system log activity observed",
};

const EXECUTIVE_IDENTITY_CONTROL_BY_FRAMEWORK = {
  GW_2SV_DISABLED: {
    SOC2: "CC6.1",
    NIST: "IA",
    FedRAMP: "IA",
    HIPAA: "164.312",
    ISO27001: "A.5",
    ISO: "A.5",
  },
  OKTA_MFA_DISABLED: {
    SOC2: "CC6.1",
    NIST: "IA",
    FedRAMP: "IA",
    HIPAA: "164.312",
    ISO27001: "A.5",
    ISO: "A.5",
  },
  GW_SUPER_ADMIN_EXPOSURE: {
    SOC2: "CC6.1",
    NIST: "AC",
    FedRAMP: "AC",
    HIPAA: "164.308",
    ISO27001: "A.5",
    ISO: "A.5",
  },
  OKTA_PRIVILEGED_GROUP_EXPOSURE: {
    SOC2: "CC6.1",
    NIST: "AC",
    FedRAMP: "AC",
    HIPAA: "164.308",
    ISO27001: "A.5",
    ISO: "A.5",
  },
  GW_INACTIVE_ADMIN: {
    SOC2: "CC6.1",
    NIST: "AC",
    FedRAMP: "AC",
    HIPAA: "164.308",
    ISO27001: "A.5",
    ISO: "A.5",
  },
  OKTA_STALE_ADMIN: {
    SOC2: "CC6.1",
    NIST: "AC",
    FedRAMP: "AC",
    HIPAA: "164.308",
    ISO27001: "A.5",
    ISO: "A.5",
  },
  GW_UNUSED_USER: {
    SOC2: "CC6.1",
    NIST: "AC",
    FedRAMP: "AC",
    HIPAA: "164.308",
    ISO27001: "A.5",
    ISO: "A.5",
  },
  OKTA_UNUSED_ACCOUNT: {
    SOC2: "CC6.1",
    NIST: "AC",
    FedRAMP: "AC",
    HIPAA: "164.308",
    ISO27001: "A.5",
    ISO: "A.5",
  },
  GW_ADMIN_COUNT: {
    SOC2: "CC6.1",
    NIST: "AC",
    FedRAMP: "AC",
    HIPAA: "164.308",
    ISO27001: "A.5",
    ISO: "A.5",
  },
  OKTA_ADMIN_EXPOSURE: {
    SOC2: "CC6.1",
    NIST: "AC",
    FedRAMP: "AC",
    HIPAA: "164.308",
    ISO27001: "A.5",
    ISO: "A.5",
  },
  GW_LOGIN_ACTIVITY_OBSERVED: {
    SOC2: "CC7.2",
    NIST: "AU",
    FedRAMP: "AU",
    HIPAA: "164.312",
    ISO27001: "A.8",
    ISO: "A.8",
  },
  OKTA_SYSTEM_LOG_ACTIVITY_OBSERVED: {
    SOC2: "CC7.2",
    NIST: "AU",
    FedRAMP: "AU",
    HIPAA: "164.312",
    ISO27001: "A.8",
    ISO: "A.8",
  },
};

function normalizeExecutiveFindingState(value) {
  return String(value || "unknown").trim().toLowerCase();
}

function isExecutiveIdentityFinding(finding = {}) {
  const source = String(finding.source || finding.provider || "").trim().toLowerCase();
  const stream = String(finding.stream || "").trim().toLowerCase();
  const ruleId = String(finding.rule_id || finding.ruleId || "").trim().toUpperCase();

  return (
    source === "google_workspace" ||
    source === "okta" ||
    stream.includes("google_workspace") ||
    stream.includes("okta") ||
    ruleId.startsWith("GW_") ||
    ruleId.startsWith("OKTA_")
  );
}

function getExecutiveIdentityProviderLabel(finding = {}) {
  const source = String(finding.source || finding.provider || "").trim().toLowerCase();
  const stream = String(finding.stream || "").trim().toLowerCase();

  if (source === "google_workspace" || stream.includes("google_workspace")) {
    return "Google Workspace";
  }

  if (source === "okta" || stream.includes("okta")) {
    return "Okta";
  }

  return "Identity";
}

function getExecutiveIdentityRuleLabel(ruleId) {
  const normalizedRule = String(ruleId || "").trim().toUpperCase();
  if (EXECUTIVE_IDENTITY_RULE_LABELS[normalizedRule]) {
    return EXECUTIVE_IDENTITY_RULE_LABELS[normalizedRule];
  }

  return normalizedRule
    ? normalizedRule
        .toLowerCase()
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ")
    : "Identity finding";
}

function getExecutiveIdentityControlId(ruleId, frameworkValue) {
  const normalizedRule = String(ruleId || "").trim().toUpperCase();
  const mapping = EXECUTIVE_IDENTITY_CONTROL_BY_FRAMEWORK[normalizedRule] || {};
  const exactFramework = String(frameworkValue || "").trim();

  return mapping[exactFramework] || mapping[exactFramework.toUpperCase()] || mapping.SOC2 || "";
}

function normalizeExecutiveIdentityFindings(findings = [], frameworkValue = "SOC2") {
  return (Array.isArray(findings) ? findings : [])
    .filter((finding) => isExecutiveIdentityFinding(finding))
    .map((finding) => {
      const ruleId = String(finding.rule_id || finding.ruleId || "").trim().toUpperCase();
      const state = normalizeExecutiveFindingState(finding.state || finding.status);
      const resourceId = String(
        finding.resource_id ||
          finding.resourceId ||
          finding.target_email ||
          finding.actor_email ||
          ""
      ).trim();

      return {
        ...finding,
        rule_id: ruleId,
        ruleId,
        label: getExecutiveIdentityRuleLabel(ruleId),
        providerLabel: getExecutiveIdentityProviderLabel(finding),
        resourceId,
        state,
        isFailing: state === "fail" || state === "failed" || state === "error",
        controlId: getExecutiveIdentityControlId(ruleId, frameworkValue),
      };
    })
    .sort((a, b) => {
      if (a.isFailing !== b.isFailing) return a.isFailing ? -1 : 1;
      return String(a.label).localeCompare(String(b.label));
    });
}

function buildExecutiveIdentitySignal(findings = [], frameworkValue = "SOC2") {
  const rows = normalizeExecutiveIdentityFindings(findings, frameworkValue);
  const failingRows = rows.filter((row) => row.isFailing);
  const passingRows = rows.filter((row) => !row.isFailing);
  const topFinding = failingRows[0] || rows[0] || null;

  const googleWorkspaceCount = rows.filter(
    (row) => row.providerLabel === "Google Workspace"
  ).length;
  const oktaCount = rows.filter((row) => row.providerLabel === "Okta").length;

  return {
    rows,
    failingRows,
    passingRows,
    topFinding,
    total: rows.length,
    failing: failingRows.length,
    passing: passingRows.length,
    googleWorkspaceCount,
    oktaCount,
  };
}

function ExecutiveIdentityRiskCard({
  signal,
  framework,
  onOpenPosture,
  onOpenDrift,
  onOpenFixPlan,
}) {
  const topFinding = signal?.topFinding || null;
  const hasFailure = safeNumber(signal?.failing) > 0;
  const provider = topFinding?.providerLabel || "Identity";
  const resource = topFinding?.resourceId || "current identity inventory";
  const controlId = topFinding?.controlId || "access-control objectives";
  const failedCount = safeNumber(signal?.failing);
  const passingCount = safeNumber(signal?.passing);
  const totalCount = safeNumber(signal?.total);
  const additionalFailures = Array.isArray(signal?.failingRows)
    ? signal.failingRows.slice(1, 4)
    : [];
  const remainingFailureCount = Math.max(0, failedCount - 1 - additionalFailures.length);

  const statusBadge = hasFailure
    ? `${failedCount} failed · ${passingCount} passing`
    : `${passingCount} passing`;

  const title = topFinding
    ? hasFailure
      ? `${topFinding.label} needs executive visibility`
      : "Identity access posture is currently passing"
    : "Identity access posture is ready for monitoring";

  const detail = topFinding
    ? hasFailure
      ? `${provider} shows ${topFinding.label.toLowerCase()} for ${resource}. This maps to ${framework} ${controlId}. Remediation should enforce the source identity control, rerun connector sync, normalize findings, and refresh posture.`
      : `${provider} evidence for ${topFinding.label.toLowerCase()} is passing. Continue monitoring connector freshness and identity inventory coverage.`
    : "Google Workspace and Okta identity checks will appear here when normalized evidence is available.";

  const borderColor = hasFailure
    ? "rgba(251,191,36,.34)"
    : "rgba(74,222,128,.30)";

  const compactFindingLine = (row) => {
    const rowResource = row?.resourceId || "population";
    const rowControl = row?.controlId ? `${framework} ${row.controlId}` : framework;
    return `${row.label} · ${rowResource} · ${rowControl}`;
  };

  return (
    <section
      className="section-card"
      style={{
        marginTop: 16,
        borderColor,
        background:
          "linear-gradient(135deg, rgba(96,165,250,.07), rgba(15,23,42,.54))",
      }}
    >
      <div className="section-header" style={{ paddingTop: 14, paddingBottom: 14 }}>
        <div>
          <div className="dash-eyebrow">OASYS · IDENTITY ACCESS RISK</div>
          <div className="section-title">Executive Identity Signal</div>
          <div className="section-subtitle">
            Identity posture summarized for leadership, with investigation and remediation one click away.
          </div>
        </div>
        <span className={`risk-pill ${hasFailure ? "risk-medium" : "risk-low"}`}>
          {statusBadge}
        </span>
      </div>

      <div
        className="card-body"
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) minmax(300px, .52fr)",
          gap: 14,
          alignItems: "stretch",
          paddingTop: 14,
          paddingBottom: 14,
        }}
      >
        <div
          style={{
            border: "1px solid rgba(148,163,184,.16)",
            borderRadius: 16,
            padding: 14,
            background: "rgba(15,23,42,.26)",
            display: "grid",
            gap: 12,
            alignContent: "start",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              alignItems: "flex-start",
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "grid", gap: 7, minWidth: 0 }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <span className="chip sm">Identity</span>
                <span className="chip sm">{provider}</span>
                {topFinding?.controlId ? (
                  <span className="chip sm">
                    {framework} {topFinding.controlId}
                  </span>
                ) : null}
              </div>
              <div
                style={{
                  color: "#f8fafc",
                  fontWeight: 900,
                  fontSize: 16,
                  lineHeight: 1.28,
                }}
              >
                {title}
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                justifyContent: "flex-end",
              }}
            >
              <span
                style={{
                  padding: "5px 10px",
                  borderRadius: 999,
                  background: hasFailure ? "rgba(239,68,68,.12)" : "rgba(16,185,129,.10)",
                  border: `1px solid ${hasFailure ? "rgba(248,113,113,.28)" : "rgba(74,222,128,.24)"}`,
                  color: hasFailure ? "#fecaca" : "#bbf7d0",
                  fontSize: 12,
                  fontWeight: 900,
                }}
              >
                {failedCount} failed
              </span>
              <span
                style={{
                  padding: "5px 10px",
                  borderRadius: 999,
                  background: "rgba(16,185,129,.10)",
                  border: "1px solid rgba(74,222,128,.22)",
                  color: "#bbf7d0",
                  fontSize: 12,
                  fontWeight: 900,
                }}
              >
                {passingCount} passing
              </span>
            </div>
          </div>

          <div style={{ color: "#cbd5e1", fontSize: 13, lineHeight: 1.55 }}>
            {detail}
          </div>

          {topFinding ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: 8,
              }}
            >
              <div className="open-item-pill" style={{ padding: "9px 10px" }}>
                <div className="open-item-label">Rule</div>
                <div className="open-item-value" style={{ fontSize: 12 }}>
                  {topFinding.ruleId}
                </div>
              </div>
              <div className="open-item-pill" style={{ padding: "9px 10px" }}>
                <div className="open-item-label">Resource</div>
                <div className="open-item-value" style={{ fontSize: 12 }}>
                  {resource}
                </div>
              </div>
              <div className="open-item-pill" style={{ padding: "9px 10px" }}>
                <div className="open-item-label">State</div>
                <div
                  className="open-item-value"
                  style={{ color: hasFailure ? "var(--danger)" : "var(--ok)", fontSize: 12 }}
                >
                  {topFinding.state || "unknown"}
                </div>
              </div>
            </div>
          ) : null}

          {additionalFailures.length > 0 ? (
            <div
              style={{
                borderTop: "1px solid rgba(148,163,184,.14)",
                paddingTop: 10,
                display: "grid",
                gap: 7,
              }}
            >
              <div className="kpi-label" style={{ color: "#A8B3C7" }}>
                ADDITIONAL FAILURES
              </div>
              {additionalFailures.map((row) => (
                <div
                  key={`${row.providerLabel}-${row.ruleId}-${row.resourceId || "population"}`}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 10,
                    color: "#A8B3C7",
                    fontSize: 12,
                    lineHeight: 1.35,
                  }}
                >
                  <span
                    title={compactFindingLine(row)}
                    style={{
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {compactFindingLine(row)}
                  </span>
                  <strong style={{ color: "var(--danger)", flex: "0 0 auto" }}>fail</strong>
                </div>
              ))}
              {remainingFailureCount > 0 ? (
                <div style={{ color: "#A8B3C7", fontSize: 12 }}>
                  +{remainingFailureCount} more failing identity finding{remainingFailureCount === 1 ? "" : "s"}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div
          style={{
            border: "1px solid rgba(148,163,184,.16)",
            borderRadius: 16,
            padding: 14,
            background: "rgba(15,23,42,.22)",
            display: "grid",
            gap: 12,
            alignContent: "start",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 8,
            }}
          >
            <ExecutiveMetricCard
              label="Checks"
              value={formatNumber(totalCount)}
              footnote={`${formatNumber(signal?.googleWorkspaceCount)} GW · ${formatNumber(signal?.oktaCount)} Okta`}
              tone="accent"
            />
            <ExecutiveMetricCard
              label="Failed"
              value={formatNumber(failedCount)}
              footnote={hasFailure ? "needs action" : "none open"}
              tone={hasFailure ? "danger" : "ok"}
            />
            <ExecutiveMetricCard
              label="Passing"
              value={formatNumber(passingCount)}
              footnote="coverage healthy"
              tone="ok"
            />
          </div>

          <div
            style={{
              borderTop: "1px solid rgba(148,163,184,.14)",
              paddingTop: 10,
              display: "grid",
              gap: 8,
              color: "#A8B3C7",
              fontSize: 12,
              lineHeight: 1.5,
            }}
          >
            <div className="kpi-label" style={{ color: "#A8B3C7" }}>
              COVERAGE SUMMARY
            </div>
            <div>
              {formatNumber(totalCount)} identity check{totalCount === 1 ? "" : "s"} monitored across Google Workspace and Okta.
            </div>
            <div>
              {formatNumber(passingCount)} passing check{passingCount === 1 ? "" : "s"} support current access-control assurance.
            </div>
            {hasFailure ? (
              <div style={{ color: "#fecaca" }}>
                {formatNumber(failedCount)} failing check{failedCount === 1 ? "" : "s"} require remediation attention.
              </div>
            ) : (
              <div style={{ color: "#bbf7d0" }}>
                No failing identity checks are currently open.
              </div>
            )}
          </div>

          <div style={{ display: "grid", gap: 8 }}>
            <button
              type="button"
              className="btn full"
              onClick={onOpenPosture}
              disabled={!topFinding}
            >
              Open in Posture
            </button>
            <button
              type="button"
              className="btn full"
              onClick={onOpenDrift}
              disabled={!topFinding}
            >
              Open in Drift & State
            </button>
            <button
              type="button"
              className="btn btn-ghost full"
              onClick={onOpenFixPlan}
              disabled={!topFinding}
            >
              Create Fix Plan
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

const EXECUTIVE_CODE_EDGE_RULE_LABELS = {
  GH_PUBLIC_REPOSITORY: "GitHub public repository exposure",
  GH_BRANCH_PROTECTION_DISABLED: "GitHub branch protection unavailable",
  GH_REQUIRED_REVIEWS_DISABLED: "GitHub required reviews unavailable",
  GH_ADMIN_BYPASS_ENABLED: "GitHub admin bypass cannot be confirmed",
  GH_SECRET_SCANNING_DISABLED: "GitHub secret scanning unavailable",
  GH_DEPENDABOT_DISABLED: "GitHub Dependabot alerts disabled",
  CF_WAF_DISABLED: "Cloudflare WAF disabled",
  CF_TLS_MIN_VERSION_WEAK: "Cloudflare weak TLS minimum",
  CF_ALWAYS_USE_HTTPS_DISABLED: "Cloudflare HTTPS enforcement disabled",
  CF_SECURITY_LEVEL_LOW: "Cloudflare security level weakened",
  CF_DNSSEC_DISABLED: "Cloudflare DNSSEC unavailable",
  CF_ZONE_ACTIVITY_OBSERVED: "Cloudflare zone inventory observed",
};

const EXECUTIVE_CODE_EDGE_CONTROL_BY_FRAMEWORK = {
  GH_PUBLIC_REPOSITORY: { SOC2: "CC6.6", NIST: "SC", FedRAMP: "SC", ISO: "A.8" },
  GH_BRANCH_PROTECTION_DISABLED: { SOC2: "CC8.1", NIST: "CM", FedRAMP: "CM", ISO: "A.8" },
  GH_REQUIRED_REVIEWS_DISABLED: { SOC2: "CC8.1", NIST: "CM", FedRAMP: "CM", ISO: "A.8" },
  GH_ADMIN_BYPASS_ENABLED: { SOC2: "CC6.1", NIST: "AC", FedRAMP: "AC", ISO: "A.5" },
  GH_SECRET_SCANNING_DISABLED: { SOC2: "CC6.6", NIST: "SI", FedRAMP: "SI", ISO: "A.8" },
  GH_DEPENDABOT_DISABLED: { SOC2: "CC7.1", NIST: "SI", FedRAMP: "SI", ISO: "A.8" },
  CF_WAF_DISABLED: { SOC2: "CC6.6", NIST: "SC", FedRAMP: "SC", ISO: "A.8" },
  CF_TLS_MIN_VERSION_WEAK: { SOC2: "CC6.6", NIST: "SC", FedRAMP: "SC", ISO: "A.8" },
  CF_ALWAYS_USE_HTTPS_DISABLED: { SOC2: "CC6.6", NIST: "SC", FedRAMP: "SC", ISO: "A.8" },
  CF_SECURITY_LEVEL_LOW: { SOC2: "CC7.1", NIST: "SI", FedRAMP: "SI", ISO: "A.8" },
  CF_DNSSEC_DISABLED: { SOC2: "CC6.6", NIST: "SC", FedRAMP: "SC", ISO: "A.8" },
  CF_ZONE_ACTIVITY_OBSERVED: { SOC2: "CC7.2", NIST: "AU", FedRAMP: "AU", ISO: "A.8" },
};

function isExecutiveCodeEdgeFinding(finding = {}) {
  const source = String(finding.source || finding.provider || "").trim().toLowerCase();
  const stream = String(finding.stream || "").trim().toLowerCase();
  const ruleId = String(finding.rule_id || finding.ruleId || "").trim().toUpperCase();

  return (
    source === "github" ||
    source === "cloudflare" ||
    stream === "github_repos" ||
    stream === "cloudflare_default" ||
    ruleId.startsWith("GH_") ||
    ruleId.startsWith("CF_")
  );
}

function getExecutiveCodeEdgeProviderLabel(finding = {}) {
  const source = String(finding.source || finding.provider || "").trim().toLowerCase();
  const stream = String(finding.stream || "").trim().toLowerCase();
  const ruleId = String(finding.rule_id || finding.ruleId || "").trim().toUpperCase();

  if (source === "cloudflare" || stream === "cloudflare_default" || ruleId.startsWith("CF_")) {
    return "Cloudflare";
  }

  if (source === "github" || stream === "github_repos" || ruleId.startsWith("GH_")) {
    return "GitHub";
  }

  return "Code & Edge";
}

function getExecutiveCodeEdgeDomainLabel(finding = {}) {
  const provider = getExecutiveCodeEdgeProviderLabel(finding);
  if (provider === "Cloudflare") return "Edge";
  if (provider === "GitHub") return "Code";
  return "Code & Edge";
}

function getExecutiveCodeEdgeRuleLabel(ruleId) {
  const normalizedRule = String(ruleId || "").trim().toUpperCase();
  if (EXECUTIVE_CODE_EDGE_RULE_LABELS[normalizedRule]) {
    return EXECUTIVE_CODE_EDGE_RULE_LABELS[normalizedRule];
  }

  return normalizedRule
    ? normalizedRule
        .toLowerCase()
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ")
    : "Code or edge finding";
}

function getExecutiveCodeEdgeControlId(ruleId, frameworkValue) {
  const normalizedRule = String(ruleId || "").trim().toUpperCase();
  const mapping = EXECUTIVE_CODE_EDGE_CONTROL_BY_FRAMEWORK[normalizedRule] || {};
  const exactFramework = String(frameworkValue || "").trim();

  return mapping[exactFramework] || mapping[exactFramework.toUpperCase()] || mapping.SOC2 || "";
}

function getExecutiveCodeEdgeSeverityWeight(finding = {}) {
  const severity = String(finding.severity || finding.severity_label || "").trim().toLowerCase();
  if (severity === "critical") return 5;
  if (severity === "high") return 4;
  if (severity === "medium") return 3;
  if (severity === "low") return 2;
  return 1;
}

function getExecutiveCodeEdgeDetail(row = {}, frameworkValue = "SOC2") {
  const provider = row.providerLabel || "Code & Edge";
  const resource = row.resourceId || "the affected resource";
  const controlId = row.controlId ? `${frameworkValue} ${row.controlId}` : frameworkValue;
  const reason = row.riskReason || row.controlEvidence || "connector evidence requires review";

  if (row.isFailing) {
    return `${provider} shows ${row.label.toLowerCase()} for ${resource}. This maps to ${controlId}. Leadership should assign remediation, rerun connector sync, normalize findings, and refresh posture.`;
  }

  if (row.isUnknown) {
    return `${provider} evidence for ${row.label.toLowerCase()} is access-limited or unknown for ${resource}. This maps to ${controlId}. Confirm API permissions or document an approved exception before treating the signal as resolved.`;
  }

  return `${provider} evidence for ${row.label.toLowerCase()} is passing for ${resource}. Continue monitoring connector freshness and source-system coverage. Evidence: ${reason}.`;
}

function normalizeExecutiveCodeEdgeFindings(findings = [], frameworkValue = "SOC2") {
  return (Array.isArray(findings) ? findings : [])
    .filter((finding) => isExecutiveCodeEdgeFinding(finding))
    .map((finding) => {
      const ruleId = String(finding.rule_id || finding.ruleId || "").trim().toUpperCase();
      const state = normalizeExecutiveFindingState(finding.state || finding.status);
      const metadata = finding.metadata || finding.raw?.metadata || {};
      const resourceId = String(
        finding.resource_id ||
          finding.resourceId ||
          finding.repository ||
          finding.zone_name ||
          finding.zoneName ||
          metadata.resource_id ||
          metadata.repository ||
          metadata.zone_name ||
          ""
      ).trim();
      const providerLabel = getExecutiveCodeEdgeProviderLabel(finding);
      const domainLabel = getExecutiveCodeEdgeDomainLabel(finding);
      const isFailing = state === "fail" || state === "failed" || state === "error";
      const isUnknown = state === "unknown" || state === "access_limited" || state === "stale";
      const isPassing = state === "pass" || state === "passed" || state === "success";

      return {
        ...finding,
        rule_id: ruleId,
        ruleId,
        label: getExecutiveCodeEdgeRuleLabel(ruleId),
        providerLabel,
        domainLabel,
        resourceId,
        state,
        isFailing,
        isUnknown,
        isPassing,
        severityWeight: getExecutiveCodeEdgeSeverityWeight(finding),
        controlId: finding.control_id || finding.controlId || getExecutiveCodeEdgeControlId(ruleId, frameworkValue),
        riskReason: metadata.risk_reason || finding.risk_reason || finding.reason_summary || "",
        controlEvidence: metadata.control_evidence || finding.control_evidence || "",
      };
    })
    .sort((a, b) => {
      const aStatePriority = a.isFailing ? 3 : a.isUnknown ? 2 : a.isPassing ? 1 : 0;
      const bStatePriority = b.isFailing ? 3 : b.isUnknown ? 2 : b.isPassing ? 1 : 0;
      if (aStatePriority !== bStatePriority) return bStatePriority - aStatePriority;
      if (a.severityWeight !== b.severityWeight) return b.severityWeight - a.severityWeight;
      return String(a.label).localeCompare(String(b.label));
    });
}

function buildExecutiveCodeEdgeSignal(findings = [], frameworkValue = "SOC2") {
  const rows = normalizeExecutiveCodeEdgeFindings(findings, frameworkValue);
  const failingRows = rows.filter((row) => row.isFailing);
  const unknownRows = rows.filter((row) => row.isUnknown);
  const passingRows = rows.filter((row) => row.isPassing);
  const topFinding = failingRows[0] || unknownRows[0] || rows[0] || null;

  const githubCount = rows.filter((row) => row.providerLabel === "GitHub").length;
  const cloudflareCount = rows.filter((row) => row.providerLabel === "Cloudflare").length;
  const codeCount = rows.filter((row) => row.domainLabel === "Code").length;
  const edgeCount = rows.filter((row) => row.domainLabel === "Edge").length;

  return {
    rows,
    failingRows,
    unknownRows,
    passingRows,
    topFinding,
    total: rows.length,
    failing: failingRows.length,
    unknown: unknownRows.length,
    passing: passingRows.length,
    githubCount,
    cloudflareCount,
    codeCount,
    edgeCount,
  };
}

function ExecutiveCodeEdgeRiskCard({
  signal,
  framework,
  onOpenPosture,
  onOpenDrift,
  onOpenFixPlan,
}) {
  const topFinding = signal?.topFinding || null;
  const failedCount = safeNumber(signal?.failing);
  const unknownCount = safeNumber(signal?.unknown);
  const passingCount = safeNumber(signal?.passing);
  const totalCount = safeNumber(signal?.total);
  const hasFailure = failedCount > 0;
  const hasUnknown = unknownCount > 0;
  const provider = topFinding?.providerLabel || "Code & Edge";
  const domain = topFinding?.domainLabel || "Code & Edge";
  const resource = topFinding?.resourceId || "current code and edge inventory";
  const additionalRows = [
    ...(Array.isArray(signal?.failingRows) ? signal.failingRows.slice(1, 4) : []),
    ...(Array.isArray(signal?.unknownRows) ? signal.unknownRows.slice(0, 3) : []),
  ].slice(0, 4);
  const remainingAttentionCount = Math.max(
    0,
    failedCount + unknownCount - (topFinding ? 1 : 0) - additionalRows.length
  );

  const statusBadge = hasFailure
    ? `${failedCount} failed · ${unknownCount} needs review`
    : hasUnknown
    ? `${unknownCount} needs review · ${passingCount} passing`
    : `${passingCount} passing`;

  const title = topFinding
    ? hasFailure
      ? `${topFinding.label} needs executive action`
      : hasUnknown
      ? `${topFinding.label} needs executive review`
      : "Code and edge posture is currently passing"
    : "Code and edge posture is ready for monitoring";

  const detail = topFinding
    ? getExecutiveCodeEdgeDetail(topFinding, framework)
    : "GitHub and Cloudflare findings will appear here when normalized connector evidence is available.";

  const borderColor = hasFailure
    ? "rgba(248,113,113,.36)"
    : hasUnknown
    ? "rgba(251,191,36,.34)"
    : "rgba(74,222,128,.30)";

  const compactFindingLine = (row) => {
    const rowResource = row?.resourceId || "resource";
    const rowControl = row?.controlId ? `${framework} ${row.controlId}` : framework;
    return `${row.providerLabel} · ${row.label} · ${rowResource} · ${rowControl}`;
  };

  return (
    <section
      className="section-card"
      style={{
        marginTop: 16,
        borderColor,
        background:
          "linear-gradient(135deg, rgba(56,189,248,.08), rgba(15,23,42,.54))",
      }}
    >
      <div className="section-header" style={{ paddingTop: 14, paddingBottom: 14 }}>
        <div>
          <div className="dash-eyebrow">OASYS · CODE & EDGE RISK</div>
          <div className="section-title">Executive Code & Edge Signal</div>
          <div className="section-subtitle">
            GitHub and Cloudflare posture summarized for leadership, with investigation and remediation one click away.
          </div>
        </div>
        <span className={`risk-pill ${hasFailure ? "risk-high" : hasUnknown ? "risk-medium" : "risk-low"}`}>
          {statusBadge}
        </span>
      </div>

      <div
        className="card-body"
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) minmax(300px, .52fr)",
          gap: 14,
          alignItems: "stretch",
          paddingTop: 14,
          paddingBottom: 14,
        }}
      >
        <div
          style={{
            border: "1px solid rgba(148,163,184,.16)",
            borderRadius: 16,
            padding: 14,
            background: "rgba(15,23,42,.26)",
            display: "grid",
            gap: 12,
            alignContent: "start",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              alignItems: "flex-start",
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "grid", gap: 7, minWidth: 0 }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <span className="chip sm">{domain}</span>
                <span className="chip sm">{provider}</span>
                {topFinding?.controlId ? (
                  <span className="chip sm">
                    {framework} {topFinding.controlId}
                  </span>
                ) : null}
              </div>
              <div
                style={{
                  color: "#f8fafc",
                  fontWeight: 900,
                  fontSize: 16,
                  lineHeight: 1.28,
                }}
              >
                {title}
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                justifyContent: "flex-end",
              }}
            >
              <span
                style={{
                  padding: "5px 10px",
                  borderRadius: 999,
                  background: hasFailure ? "rgba(239,68,68,.12)" : "rgba(16,185,129,.10)",
                  border: `1px solid ${hasFailure ? "rgba(248,113,113,.28)" : "rgba(74,222,128,.24)"}`,
                  color: hasFailure ? "#fecaca" : "#bbf7d0",
                  fontSize: 12,
                  fontWeight: 900,
                }}
              >
                {failedCount} failed
              </span>
              <span
                style={{
                  padding: "5px 10px",
                  borderRadius: 999,
                  background: hasUnknown ? "rgba(245,158,11,.12)" : "rgba(16,185,129,.10)",
                  border: `1px solid ${hasUnknown ? "rgba(251,191,36,.28)" : "rgba(74,222,128,.22)"}`,
                  color: hasUnknown ? "#fde68a" : "#bbf7d0",
                  fontSize: 12,
                  fontWeight: 900,
                }}
              >
                {unknownCount} needs review
              </span>
            </div>
          </div>

          <div style={{ color: "#cbd5e1", fontSize: 13, lineHeight: 1.55 }}>
            {detail}
          </div>

          {topFinding ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: 8,
              }}
            >
              <div className="open-item-pill" style={{ padding: "9px 10px" }}>
                <div className="open-item-label">Rule</div>
                <div className="open-item-value" style={{ fontSize: 12 }}>
                  {topFinding.ruleId}
                </div>
              </div>
              <div className="open-item-pill" style={{ padding: "9px 10px" }}>
                <div className="open-item-label">Resource</div>
                <div className="open-item-value" style={{ fontSize: 12 }}>
                  {resource}
                </div>
              </div>
              <div className="open-item-pill" style={{ padding: "9px 10px" }}>
                <div className="open-item-label">State</div>
                <div
                  className="open-item-value"
                  style={{
                    color: topFinding.isFailing
                      ? "var(--danger)"
                      : topFinding.isUnknown
                      ? "var(--warn)"
                      : "var(--ok)",
                    fontSize: 12,
                  }}
                >
                  {topFinding.state || "unknown"}
                </div>
              </div>
            </div>
          ) : null}

          {additionalRows.length > 0 ? (
            <div
              style={{
                borderTop: "1px solid rgba(148,163,184,.14)",
                paddingTop: 10,
                display: "grid",
                gap: 7,
              }}
            >
              <div className="kpi-label" style={{ color: "#A8B3C7" }}>
                ADDITIONAL CODE / EDGE ATTENTION
              </div>
              {additionalRows.map((row) => (
                <div
                  key={`${row.providerLabel}-${row.ruleId}-${row.resourceId || "resource"}`}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 10,
                    color: "#A8B3C7",
                    fontSize: 12,
                    lineHeight: 1.35,
                  }}
                >
                  <span
                    title={compactFindingLine(row)}
                    style={{
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {compactFindingLine(row)}
                  </span>
                  <strong
                    style={{
                      color: row.isFailing ? "var(--danger)" : "var(--warn)",
                      flex: "0 0 auto",
                    }}
                  >
                    {row.isFailing ? "fail" : "review"}
                  </strong>
                </div>
              ))}
              {remainingAttentionCount > 0 ? (
                <div style={{ color: "#A8B3C7", fontSize: 12 }}>
                  +{remainingAttentionCount} more code/edge finding{remainingAttentionCount === 1 ? "" : "s"} needing attention
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div
          style={{
            border: "1px solid rgba(148,163,184,.16)",
            borderRadius: 16,
            padding: 14,
            background: "rgba(15,23,42,.22)",
            display: "grid",
            gap: 12,
            alignContent: "start",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 8,
            }}
          >
            <ExecutiveMetricCard
              label="Checks"
              value={formatNumber(totalCount)}
              footnote={`${formatNumber(signal?.githubCount)} GitHub · ${formatNumber(signal?.cloudflareCount)} Cloudflare`}
              tone="accent"
            />
            <ExecutiveMetricCard
              label="Failed"
              value={formatNumber(failedCount)}
              footnote={hasFailure ? "needs action" : "none open"}
              tone={hasFailure ? "danger" : "ok"}
            />
            <ExecutiveMetricCard
              label="Needs Review"
              value={formatNumber(unknownCount)}
              footnote="access-limited / unknown"
              tone={hasUnknown ? "warn" : "ok"}
            />
          </div>

          <div
            style={{
              borderTop: "1px solid rgba(148,163,184,.14)",
              paddingTop: 10,
              display: "grid",
              gap: 8,
              color: "#A8B3C7",
              fontSize: 12,
              lineHeight: 1.5,
            }}
          >
            <div className="kpi-label" style={{ color: "#A8B3C7" }}>
              COVERAGE SUMMARY
            </div>
            <div>
              {formatNumber(totalCount)} code/edge check{totalCount === 1 ? "" : "s"} monitored across GitHub and Cloudflare.
            </div>
            <div>
              {formatNumber(passingCount)} passing check{passingCount === 1 ? "" : "s"} support current code and edge assurance.
            </div>
            {hasFailure ? (
              <div style={{ color: "#fecaca" }}>
                {formatNumber(failedCount)} failing check{failedCount === 1 ? "" : "s"} require remediation attention.
              </div>
            ) : null}
            {hasUnknown ? (
              <div style={{ color: "#fde68a" }}>
                {formatNumber(unknownCount)} check{unknownCount === 1 ? "" : "s"} need access review or approved exception handling.
              </div>
            ) : null}
          </div>

          <div style={{ display: "grid", gap: 8 }}>
            <button
              type="button"
              className="btn full"
              onClick={onOpenPosture}
              disabled={!topFinding}
            >
              Open in Posture
            </button>
            <button
              type="button"
              className="btn full"
              onClick={onOpenDrift}
              disabled={!topFinding}
            >
              Open in Drift & State
            </button>
            <button
              type="button"
              className="btn btn-ghost full"
              onClick={onOpenFixPlan}
              disabled={!topFinding}
            >
              Create Fix Plan
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}


function buildExecutiveWorkflowSignal(summary = null) {
  const data = summary && typeof summary === "object" ? summary : {};
  const total = safeNumber(data.total);
  const running = safeNumber(data.running);
  const queued = safeNumber(data.queued);
  const blocked = safeNumber(data.blocked ?? data.blocked_count);
  const awaitingApproval = safeNumber(data.awaiting_approval ?? data.approval_waiting_count);
  const awaitingValidation = safeNumber(data.awaiting_validation ?? data.validation_waiting_count);
  const completedToday = safeNumber(data.completed_today);
  const createdToday = safeNumber(data.created_today);
  const dependencyPending = safeNumber(data.dependency_pending_count);
  const dependencyBlocked = safeNumber(data.dependency_blocked_count);
  const dependencyFailed = safeNumber(data.dependency_failed_count);
  const dependencyIssues = dependencyPending + dependencyBlocked + dependencyFailed;
  const failed = safeNumber(data.failed);
  const cancelled = safeNumber(data.cancelled);
  const averageDurationSeconds = safeNumber(data.average_duration_seconds, null);
  const bottlenecks = Array.isArray(data.bottlenecks_by_step_type)
    ? data.bottlenecks_by_step_type
    : [];
  const topBottleneck = bottlenecks[0] || null;

  const hasCriticalBlocker = blocked > 0 || dependencyBlocked > 0 || dependencyFailed > 0 || failed > 0;
  const hasWaitingBurden = awaitingApproval > 0 || awaitingValidation > 0 || dependencyPending > 0;
  const tone = hasCriticalBlocker ? "danger" : hasWaitingBurden ? "warn" : "ok";
  const label = hasCriticalBlocker ? "Blocked" : hasWaitingBurden ? "Needs attention" : "Healthy";

  return {
    loaded: Boolean(summary),
    total,
    running,
    queued,
    blocked,
    awaitingApproval,
    awaitingValidation,
    completedToday,
    createdToday,
    dependencyPending,
    dependencyBlocked,
    dependencyFailed,
    dependencyIssues,
    failed,
    cancelled,
    averageDurationSeconds,
    bottlenecks,
    topBottleneck,
    tone,
    label,
  };
}

function ExecutiveWorkflowSignalCard({ signal, onOpenWorkflowCenter }) {
  const loaded = Boolean(signal?.loaded);
  const tone = signal?.tone || "default";
  const hasIssues = tone === "danger" || tone === "warn";
  const topBottleneck = signal?.topBottleneck || null;
  const bottleneckLabel = topBottleneck?.step_type
    ? String(topBottleneck.step_type).replace(/_/g, " ")
    : "No active bottleneck";
  const dependencyDetail = safeNumber(signal?.dependencyIssues) > 0
    ? `${formatNumber(signal.dependencyIssues)} dependency item${safeNumber(signal.dependencyIssues) === 1 ? "" : "s"} need review.`
    : "Workflow dependencies are currently clear.";

  const statusBadge = loaded
    ? `${formatNumber(signal.running)} running · ${formatNumber(signal.blocked)} blocked`
    : "loading";

  return (
    <section
      className="section-card"
      style={{
        marginTop: 16,
        borderColor:
          tone === "danger"
            ? "rgba(248,113,113,.36)"
            : tone === "warn"
            ? "rgba(251,191,36,.34)"
            : "rgba(74,222,128,.30)",
        background:
          "linear-gradient(135deg, rgba(96,165,250,.08), rgba(15,23,42,.54))",
      }}
    >
      <div className="section-header" style={{ paddingTop: 14, paddingBottom: 14 }}>
        <div>
          <div className="dash-eyebrow">OASYS · WORKFLOW ORCHESTRATION</div>
          <div className="section-title">Executive Workflow Signal</div>
          <div className="section-subtitle">
            Autonomous workflow execution summarized for leadership, including blockers, approvals, validation readiness, and dependency health.
          </div>
        </div>
        <span className={`risk-pill ${tone === "danger" ? "risk-high" : tone === "warn" ? "risk-medium" : "risk-low"}`}>
          {loaded ? signal.label : statusBadge}
        </span>
      </div>

      <div
        className="card-body"
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) minmax(300px, .52fr)",
          gap: 14,
          alignItems: "stretch",
          paddingTop: 14,
          paddingBottom: 14,
        }}
      >
        <div
          style={{
            border: "1px solid rgba(148,163,184,.16)",
            borderRadius: 16,
            padding: 14,
            background: "rgba(15,23,42,.26)",
            display: "grid",
            gap: 12,
            alignContent: "start",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              alignItems: "flex-start",
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "grid", gap: 7, minWidth: 0 }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <span className="chip sm">Workflow</span>
                <span className="chip sm">Orchestration</span>
                <span className="chip sm">Dependency Aware</span>
              </div>
              <div
                style={{
                  color: "#f8fafc",
                  fontWeight: 900,
                  fontSize: 16,
                  lineHeight: 1.28,
                }}
              >
                {hasIssues
                  ? "Workflow operations need leadership attention"
                  : "Workflow operations are running without blockers"}
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                justifyContent: "flex-end",
              }}
            >
              <span
                style={{
                  padding: "5px 10px",
                  borderRadius: 999,
                  background: "rgba(96,165,250,.12)",
                  border: "1px solid rgba(96,165,250,.28)",
                  color: "#bfdbfe",
                  fontSize: 12,
                  fontWeight: 900,
                }}
              >
                {formatNumber(signal?.running)} running
              </span>
              <span
                style={{
                  padding: "5px 10px",
                  borderRadius: 999,
                  background: hasIssues ? "rgba(245,158,11,.12)" : "rgba(16,185,129,.10)",
                  border: `1px solid ${hasIssues ? "rgba(251,191,36,.28)" : "rgba(74,222,128,.22)"}`,
                  color: hasIssues ? "#fde68a" : "#bbf7d0",
                  fontSize: 12,
                  fontWeight: 900,
                }}
              >
                {formatNumber(signal?.dependencyIssues)} dependency issues
              </span>
            </div>
          </div>

          <div style={{ color: "#cbd5e1", fontSize: 13, lineHeight: 1.55 }}>
            OASYS is currently coordinating {formatNumber(signal?.total)} workflow{safeNumber(signal?.total) === 1 ? "" : "s"}, with {formatNumber(signal?.queued)} queued for execution and {formatNumber(signal?.completedToday)} completed today. {dependencyDetail}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 8,
            }}
          >
            <div className="open-item-pill" style={{ padding: "9px 10px" }}>
              <div className="open-item-label">Bottleneck</div>
              <div className="open-item-value" style={{ fontSize: 12, textTransform: "capitalize" }}>
                {bottleneckLabel}
              </div>
            </div>
            <div className="open-item-pill" style={{ padding: "9px 10px" }}>
              <div className="open-item-label">Approval Wait</div>
              <div className="open-item-value" style={{ fontSize: 12, color: safeNumber(signal?.awaitingApproval) > 0 ? "var(--warn)" : "var(--ok)" }}>
                {formatNumber(signal?.awaitingApproval)}
              </div>
            </div>
            <div className="open-item-pill" style={{ padding: "9px 10px" }}>
              <div className="open-item-label">Validation Wait</div>
              <div className="open-item-value" style={{ fontSize: 12, color: safeNumber(signal?.awaitingValidation) > 0 ? "var(--warn)" : "var(--ok)" }}>
                {formatNumber(signal?.awaitingValidation)}
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            border: "1px solid rgba(148,163,184,.16)",
            borderRadius: 16,
            padding: 14,
            background: "rgba(15,23,42,.22)",
            display: "grid",
            gap: 12,
            alignContent: "start",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 8,
            }}
          >
            <ExecutiveMetricCard
              label="Workflows"
              value={formatNumber(signal?.total)}
              footnote={`${formatNumber(signal?.createdToday)} created today`}
              tone="accent"
            />
            <ExecutiveMetricCard
              label="Blocked"
              value={formatNumber(signal?.blocked)}
              footnote="active blockers"
              tone={safeNumber(signal?.blocked) > 0 ? "danger" : "ok"}
            />
            <ExecutiveMetricCard
              label="Queued"
              value={formatNumber(signal?.queued)}
              footnote="ready to run"
              tone={safeNumber(signal?.queued) > 0 ? "warn" : "ok"}
            />
          </div>

          <div
            style={{
              borderTop: "1px solid rgba(148,163,184,.14)",
              paddingTop: 10,
              display: "grid",
              gap: 8,
              color: "#A8B3C7",
              fontSize: 12,
              lineHeight: 1.5,
            }}
          >
            <div className="kpi-label" style={{ color: "#A8B3C7" }}>
              OPERATING SUMMARY
            </div>
            <div>
              {formatNumber(signal?.running)} active workflow{safeNumber(signal?.running) === 1 ? "" : "s"} are currently in motion.
            </div>
            <div>
              Average completed workflow duration: {formatDurationSeconds(signal?.averageDurationSeconds)}.
            </div>
            {safeNumber(signal?.dependencyIssues) > 0 ? (
              <div style={{ color: "#fde68a" }}>{dependencyDetail}</div>
            ) : (
              <div style={{ color: "#bbf7d0" }}>No dependency blockers are currently open.</div>
            )}
          </div>

          <div style={{ display: "grid", gap: 8 }}>
            <button
              type="button"
              className="btn full"
              onClick={onOpenWorkflowCenter}
            >
              Open Workflow Center
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function ExecutiveIntelligencePage({
  apiBaseUrl,
  getAccessToken,
  themeMode = "light",
  userProfile = null,
  routeParams = {},
  setMode,
}) {
  const { getAccessTokenSilently } = useAuth0();
  const { setCopilotContext, clearCopilotContext } = useAdvisor();

  const [framework, setFramework] = useState(routeParams?.framework || "SOC2");
  const [frameworkScope, setFrameworkScope] = useState(null);
  const [windowValue, setWindowValue] = useState(routeParams?.window || "30d");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const [exportNotice, setExportNotice] = useState("");

  const [summary, setSummary] = useState(null);
  const [remediationAnalytics, setRemediationAnalytics] = useState(null);
  const [frameworkHealth, setFrameworkHealth] = useState(null);
  const [trends, setTrends] = useState(null);
  const [leadershipActions, setLeadershipActions] = useState([]);
  const [identityFindings, setIdentityFindings] = useState([]);
  const [workflowSummary, setWorkflowSummary] = useState(null);
  const [executiveExplain, setExecutiveExplain] = useState({
    open: false,
    action: null,
    actionIndex: null,
    question: EXECUTIVE_EXPLAIN_PROMPTS[0],
    card: null,
    metadata: null,
    loading: false,
    error: "",
  });
  const leadershipActionBriefingRef = useRef(null);
  const leadershipReturnHandledRef = useRef("");

  useEffect(() => {
    const action =
      executiveExplain.open && executiveExplain.action
        ? executiveExplain.action
        : null;

    const pageContext = {
      page: "executive",
      surface: "executive",
      intelligence_mode: action ? "contextual_explain" : "page",
      framework,
      window: windowValue,
    };

    if (!action) {
      clearCopilotContext();
      setCopilotContext(pageContext);
      return;
    }

    const actionKey = String(action?.key || "").trim();
    const actionTitle = String(action?.title || "").trim();

    setCopilotContext({
      ...pageContext,
      record_type: "leadership_action",
      ...(actionKey ? { leadership_action_key: actionKey } : {}),
      ...(actionTitle ? { leadership_action_title: actionTitle } : {}),
      ...(Number.isInteger(executiveExplain.actionIndex)
        ? { leadership_action_index: executiveExplain.actionIndex }
        : {}),
      focus_label: actionTitle || "Selected leadership action",
      focus_type: "Leadership action",
    });
  }, [
    clearCopilotContext,
    executiveExplain.action,
    executiveExplain.actionIndex,
    executiveExplain.open,
    framework,
    setCopilotContext,
    windowValue,
  ]);

  const returnSection = String(
    routeParams?.returnSection || routeParams?.return_section || ""
  )
    .trim()
    .toLowerCase();

  const normalizedBase = useMemo(() => {
    const base = (apiBaseUrl || "").trim();
    return base.endsWith("/") ? base.slice(0, -1) : base;
  }, [apiBaseUrl]);

  const tenantId =
    userProfile?.tenant_id ||
    userProfile?.tenantId ||
    process.env.REACT_APP_TENANT_ID ||
    "";

  const userId =
    userProfile?.user_id ||
    userProfile?.userId ||
    userProfile?.id ||
    userProfile?.sub ||
    process.env.REACT_APP_USER_ID ||
    "";

  const fetchToken = useCallback(async () => {
    if (typeof getAccessToken === "function") return await getAccessToken();

    const audience = process.env.REACT_APP_AUTH0_AUDIENCE;
    return await getAccessTokenSilently(
      audience ? { authorizationParams: { audience } } : undefined
    );
  }, [getAccessToken, getAccessTokenSilently]);

  const getJsonWithToken = useCallback(
    async (token, path, params = {}, signal) => {
      const searchParams = new URLSearchParams();

      Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          searchParams.set(key, String(value));
        }
      });

      const url = searchParams.toString()
        ? `${normalizedBase}${path}?${searchParams.toString()}`
        : `${normalizedBase}${path}`;

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      if (tenantId) headers["X-Tenant-Id"] = tenantId;
      if (userId) headers["X-User-Id"] = userId;

      const res = await fetch(url, { method: "GET", headers, signal });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`${res.status} ${res.statusText}: ${text}`);
      }

      return res.json();
    },
    [normalizedBase, tenantId, userId]
  );

  const getBlobWithToken = useCallback(
    async (token, path, params = {}, signal) => {
      const searchParams = new URLSearchParams();

      Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          searchParams.set(key, String(value));
        }
      });

      const url = searchParams.toString()
        ? `${normalizedBase}${path}?${searchParams.toString()}`
        : `${normalizedBase}${path}`;

      const headers = { Authorization: `Bearer ${token}` };
      if (tenantId) headers["X-Tenant-Id"] = tenantId;
      if (userId) headers["X-User-Id"] = userId;

      const res = await fetch(url, { method: "GET", headers, signal });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`${res.status} ${res.statusText}: ${text}`);
      }

      return res.blob();
    },
    [normalizedBase, tenantId, userId]
  );

  // Resolve persisted tenant framework authority before loading analytics.
  // Historical analytics never define which frameworks are authorized.
  useEffect(() => {
    let active = true;
    const controller = new AbortController();

    setFrameworkScope(null);
    setLoading(true);
    setError("");

    (async () => {
      try {
        if (!tenantId || !userId) {
          throw new Error(
            "Executive Intelligence requires tenant and user identifiers."
          );
        }

        const token = await fetchToken();
        const result = await getJsonWithToken(
          token,
          "/dashboard/applied-frameworks",
          {},
          controller.signal
        );

        if (!active) return;

        const frameworks = Array.isArray(result?.frameworks)
          ? result.frameworks
              .filter((value) => typeof value === "string" && value.trim())
              .map((value) => value.trim())
              .filter(
                (value, index, values) =>
                  values.findIndex(
                    (other) =>
                      normalizeFrameworkKey(other) ===
                      normalizeFrameworkKey(value)
                  ) === index
              )
          : [];

        if (!frameworks.length) {
          throw new Error(
            "No applied frameworks are configured for this organization."
          );
        }

        // Keep an authorized deep-link choice; otherwise choose the
        // organization's first applied framework, never a global default.
        setFramework((previous) =>
          frameworks.find(
            (value) =>
              normalizeFrameworkKey(value) ===
              normalizeFrameworkKey(previous)
          ) || frameworks[0]
        );
        setFrameworkScope({ tenantId, frameworks });
      } catch (e) {
        if (!active || controller.signal.aborted) return;
        setFrameworkScope(null);
        setError(`Framework Authority: ${String(e?.message || e)}`);
        setLoading(false);
      }
    })();

    return () => {
      active = false;
      controller.abort();
    };
  }, [fetchToken, getJsonWithToken, tenantId, userId]);

  const appliedFrameworks =
    frameworkScope?.tenantId === tenantId
      ? frameworkScope.frameworks
      : [];

  useEffect(() => {
    // Do not request executive analytics until organization authority
    // confirms the framework selected for this tenant.
    if (
      !frameworkScope ||
      frameworkScope.tenantId !== tenantId ||
      !frameworkScope.frameworks.some(
        (value) =>
          normalizeFrameworkKey(value) === normalizeFrameworkKey(framework)
      )
    ) {
      return undefined;
    }

    let mounted = true;
    const controller = new AbortController();
    const timeoutId = window.setTimeout(
      () => controller.abort("executive-intelligence-timeout"),
      REQUEST_TIMEOUT_MS
    );

    setLoading(true);
    setError("");

    (async () => {
      try {
        if (!tenantId || !userId) {
          throw new Error("Executive Intelligence requires tenant and user identifiers.");
        }

        const token = await fetchToken();
        const params = { framework, window: windowValue };

        const [briefingResult, identityResult, workflowSummaryResult] = await Promise.allSettled([
          getJsonWithToken(token, "/executive/briefing", params, controller.signal),
          getJsonWithToken(token, "/normalize/findings", { limit: 250 }, controller.signal),
          getJsonWithToken(token, "/workflows/summary", {}, controller.signal),
        ]);

        if (briefingResult.status !== "fulfilled") {
          throw briefingResult.reason;
        }

        const briefingPayload = briefingResult.value || {};
        const rawPayloads = briefingPayload?.raw_payloads || {};
        const identityPayload =
          identityResult.status === "fulfilled" ? identityResult.value || {} : {};
        const workflowSummaryPayload =
          workflowSummaryResult.status === "fulfilled" ? workflowSummaryResult.value || {} : null;

        if (identityResult.status === "rejected") {
          console.warn("Executive identity signal unavailable:", identityResult.reason);
        }
        if (workflowSummaryResult.status === "rejected") {
          console.warn("Executive workflow signal unavailable:", workflowSummaryResult.reason);
        }

        if (!mounted) return;

        setSummary(rawPayloads.summary || null);
        setRemediationAnalytics(rawPayloads.remediation_analytics || null);
        setFrameworkHealth(rawPayloads.framework_health || null);
        setTrends(rawPayloads.trends || null);
        setLeadershipActions(
          Array.isArray(briefingPayload?.recommended_actions)
            ? briefingPayload.recommended_actions
            : []
        );
        setIdentityFindings(
          Array.isArray(identityPayload?.findings)
            ? identityPayload.findings
            : Array.isArray(identityPayload?.items)
            ? identityPayload.items
            : []
        );
        setWorkflowSummary(workflowSummaryPayload);
      } catch (err) {
        if (!mounted) return;
        console.error("Executive Intelligence load error:", err);
        setError(String(err?.message || err || "Unable to load Executive Intelligence."));
      } finally {
        window.clearTimeout(timeoutId);
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
      window.clearTimeout(timeoutId);
      controller.abort("executive-intelligence-unmounted");
    };
  }, [fetchToken, framework, frameworkScope, getJsonWithToken, tenantId, userId, windowValue]);

  useEffect(() => {
    if (!exportNotice) return undefined;

    const timeoutId = window.setTimeout(() => {
      setExportNotice("");
    }, EXPORT_NOTICE_TIMEOUT_MS);

    return () => window.clearTimeout(timeoutId);
  }, [exportNotice]);

  useEffect(() => {
    if (loading || returnSection !== "leadership_action_briefing") return undefined;

    const navigationKey = [
      returnSection,
      routeParams?.routeNavNonce || routeParams?.navNonce || "",
      framework,
      windowValue,
    ].join("::");

    if (leadershipReturnHandledRef.current === navigationKey) return undefined;
    leadershipReturnHandledRef.current = navigationKey;

    const scrollToBriefing = () => {
      const target = leadershipActionBriefingRef.current;
      if (!target) return;

      try {
        target.scrollIntoView({ behavior: "auto", block: "start" });
      } catch {
        const top = target.getBoundingClientRect().top + window.scrollY - 96;
        window.scrollTo(0, Math.max(0, top));
      }
    };

    const firstFrame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(scrollToBriefing);
    });
    const shortDelay = window.setTimeout(scrollToBriefing, 80);
    const layoutDelay = window.setTimeout(scrollToBriefing, 180);

    return () => {
      window.cancelAnimationFrame(firstFrame);
      window.clearTimeout(shortDelay);
      window.clearTimeout(layoutDelay);
    };
  }, [
    framework,
    loading,
    returnSection,
    routeParams?.navNonce,
    routeParams?.routeNavNonce,
    windowValue,
  ]);

  const riskVelocity = useMemo(() => summary?.risk_velocity || {}, [summary]);
  const throughput = useMemo(
    () => remediationAnalytics?.throughput || summary?.remediation_throughput || {},
    [remediationAnalytics, summary]
  );
  const validation = useMemo(
    () => remediationAnalytics?.validation || {},
    [remediationAnalytics]
  );
  const sla = useMemo(() => remediationAnalytics?.sla || {}, [remediationAnalytics]);
  const validationMetrics = useMemo(() => buildValidationExecutiveMetrics(validation), [validation]);
  const frameworkRows = useMemo(
    () =>
      deduplicateFrameworkRows(
        Array.isArray(frameworkHealth?.frameworks)
          ? frameworkHealth.frameworks
          : []
      ),
    [frameworkHealth]
  );
  const rawPostureTrend = useMemo(
    () => (Array.isArray(trends?.posture) ? trends.posture : []),
    [trends]
  );
  const postureTrend = useMemo(() => compressPostureTrendByDay(rawPostureTrend), [rawPostureTrend]);
  const validationTrend = useMemo(
    () => (Array.isArray(trends?.validation) ? trends.validation : []),
    [trends]
  );
  const throughputTrend = useMemo(
    () => (Array.isArray(trends?.throughput) ? trends.throughput : []),
    [trends]
  );
  const latestPosture = safeNumber(summary?.overall_posture);
  const riskMeta = getRiskLabel(latestPosture);
  const trendSummary = buildTrendSummary(postureTrend);
  const executiveDriver = useMemo(
    () =>
      buildExecutiveDriver({
        riskVelocity,
        throughput,
        validationMetrics,
        trendSummary,
      }),
    [riskVelocity, throughput, validationMetrics, trendSummary]
  );

  const selectedFrameworkHealth =
    frameworkRows.find(
      (row) => normalizeFrameworkKey(row.framework) === normalizeFrameworkKey(framework)
    ) ||
    frameworkRows[0] ||
    null;

  const executiveIdentitySignal = useMemo(
    () => buildExecutiveIdentitySignal(identityFindings, framework),
    [identityFindings, framework]
  );

  const executiveCodeEdgeSignal = useMemo(
    () => buildExecutiveCodeEdgeSignal(identityFindings, framework),
    [identityFindings, framework]
  );

  const executiveWorkflowSignal = useMemo(
    () => buildExecutiveWorkflowSignal(workflowSummary),
    [workflowSummary]
  );

  const strategicSignals = useMemo(() => {
    const signals = [];
    const tasksOpened = safeNumber(throughput?.tasks_opened);
    const tasksClosed = safeNumber(throughput?.tasks_closed);
    const hasRemediationActivity = tasksOpened > 0 || tasksClosed > 0;
    const hasValidationActivity = validationMetrics.runs > 0;

    signals.push({
      eyebrow: "What changed",
      title: executiveDriver.label,
      detail: executiveDriver.detail,
      tone: executiveDriver.tone,
    });

    signals.push(
      hasRemediationActivity
        ? {
            eyebrow: "Remediation efficiency",
            title: `${formatDays(summary?.mttr_days)} MTTR with ${formatPercent(
              throughput?.closure_rate
            )} closure rate`,
            detail: `${formatNumber(tasksOpened)} tasks opened and ${formatNumber(
              tasksClosed
            )} tasks closed in the selected window. ${
              tasksOpened > tasksClosed
                ? "Closure velocity should be watched because intake remains ahead of closure."
                : "Recorded closure activity is keeping pace with intake."
            }`,
            tone: safeNumber(throughput?.closure_rate) >= 50 ? "ok" : "warn",
          }
        : {
            eyebrow: "Remediation efficiency",
            title: "No remediation activity in this window",
            detail:
              "No tasks were opened or closed during the selected period. Choose a longer reporting window to review earlier remediation movement.",
            tone: "default",
          }
    );

    signals.push(
      hasValidationActivity
        ? {
            eyebrow: "Governance signal",
            title: `${formatPercent(
              validationMetrics.acceptanceRate
            )} validation acceptance`,
            detail: `${formatNumber(
              validationMetrics.passed
            )} passed validations, ${formatNumber(
              validationMetrics.approvedExceptions
            )} approved exceptions, ${formatNumber(
              validationMetrics.manualOverrides
            )} manual overrides, and ${formatNumber(
              validationMetrics.failed
            )} failed validations were recorded.`,
            tone:
              validationMetrics.acceptanceRate >= 80
                ? "ok"
                : validationMetrics.acceptanceRate >= 50
                ? "warn"
                : "danger",
          }
        : {
            eyebrow: "Governance signal",
            title: "No validation outcomes in this window",
            detail:
              "Acceptance cannot be evaluated because no validation runs were recorded during the selected period.",
            tone: "default",
          }
    );

    signals.push(
      hasValidationActivity
        ? {
            eyebrow: "Executive burden",
            title: `${formatPercent(
              validationMetrics.governanceBurdenRate
            )} governance burden`,
            detail: `${formatNumber(
              validationMetrics.governanceBurden
            )} of ${formatNumber(
              validationMetrics.runs
            )} validation outcomes required approved exception handling or manual override review.`,
            tone:
              validationMetrics.governanceBurdenRate > 50
                ? "warn"
                : validationMetrics.governanceBurdenRate > 0
                ? "default"
                : "ok",
          }
        : {
            eyebrow: "Executive burden",
            title: "No governance burden measured",
            detail:
              "No validation outcomes required exception or manual-override review because no validation activity occurred in this window.",
            tone: "default",
          }
    );

    return signals;
  }, [
    executiveDriver,
    summary?.mttr_days,
    throughput?.closure_rate,
    throughput?.tasks_opened,
    throughput?.tasks_closed,
    validationMetrics,
  ]);

  const handleExportSummary = useCallback(async () => {
    if (exporting) return;

    setExporting(true);
    setExportError("");
    setExportNotice("");

    try {
      const token = await fetchToken();
      const payload = await getJsonWithToken(
        token,
        "/executive/export",
        {
          framework,
          window: windowValue,
          format: "json",
        }
      );

      const filename = buildDownloadFilename({ framework, windowValue });
      const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");

      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);

      setExportNotice(`Export downloaded: ${filename}`);
    } catch (err) {
      console.error("Executive Intelligence export error:", err);
      setExportError(String(err?.message || err || "Unable to export Executive Intelligence."));
    } finally {
      setExporting(false);
    }
  }, [exporting, fetchToken, framework, getJsonWithToken, windowValue]);

  const handleDownloadPdfReport = useCallback(async () => {
    if (exporting || pdfExporting) return;

    setPdfExporting(true);
    setExportError("");
    setExportNotice("");

    try {
      const token = await fetchToken();
      const blob = await getBlobWithToken(token, "/executive/report.pdf", {
        framework,
        window: windowValue,
      });
      const filename = buildPdfDownloadFilename({ framework, windowValue });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");

      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);

      setExportNotice(`PDF report downloaded: ${filename}`);
    } catch (err) {
      console.error("Executive Intelligence PDF report error:", err);
      setExportError(String(err?.message || err || "Unable to download PDF report."));
    } finally {
      setPdfExporting(false);
    }
  }, [exporting, fetchToken, framework, getBlobWithToken, pdfExporting, windowValue]);

  const handleLeadershipAction = useCallback(
    (action) => {
      const workflow = action?.workflow || {};
      const targetMode = workflow?.mode || "posture";

      if (targetMode === "dashboard") {
        setMode?.("dashboard", {
          source: "executive_intelligence",
          routeNavNonce: String(Date.now()),
          scrollTop: "1",
        });
        return;
      }

      const remediationQueue =
        workflow?.remediationQueue || workflow?.remediation_queue || "";

      setMode?.("posture", {
        framework,
        window: windowValue,
        section: workflow?.section || undefined,
        remediationQueue: remediationQueue || undefined,
        source: "executive_intelligence",
        sourceMode: "executive",
        returnMode: "executive",
        returnTo: "executive_intelligence",
        returnSection: "leadership_action_briefing",
        routeNavNonce: String(Date.now()),
      });
    },
    [framework, setMode, windowValue]
  );

  const askExecutiveActionExplain = useCallback(
    async (
      action,
      actionIndex,
      question = EXECUTIVE_EXPLAIN_PROMPTS[0]
    ) => {
      if (!action || typeof action !== "object") return;

      const actionKey = String(action?.key || "").trim();
      const actionTitle = String(action?.title || "").trim();

      if (!actionKey && !actionTitle) {
        setExecutiveExplain((current) => ({
          ...current,
          loading: false,
          error:
            "OASYS Intelligence could not identify this canonical leadership action.",
        }));
        return;
      }

      setExecutiveExplain((current) => ({
        ...current,
        open: true,
        action,
        actionIndex,
        question,
        loading: true,
        error: "",
      }));

      try {
        const token = await fetchToken();
        const headers = {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        };

        if (tenantId) headers["X-Tenant-Id"] = tenantId;
        if (userId) headers["X-User-Id"] = userId;

        const response = await fetch(`${normalizedBase}/ai/copilot/ask`, {
          method: "POST",
          headers,
          body: JSON.stringify({
            question,
            scope: "executive",
            copilot_context: {
              page: "executive",
              surface: "executive",
              intelligence_mode: "contextual_explain",
              record_type: "leadership_action",
              framework,
              window: windowValue,
              leadership_action_key: actionKey || undefined,
              leadership_action_title: actionTitle || undefined,
              leadership_action_index:
                Number.isInteger(actionIndex) ? actionIndex : undefined,
            },
          }),
        });

        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            payload?.detail ||
              payload?.message ||
              `Executive explanation request failed (${response.status}).`
          );
        }

        const card =
          payload?.card && typeof payload.card === "object"
            ? payload.card
            : null;

        if (!card) {
          throw new Error(
            "OASYS Intelligence returned no grounded Executive explanation card."
          );
        }

        setExecutiveExplain((current) => ({
          ...current,
          action,
          actionIndex,
          question,
          card,
          metadata:
            card?.metadata && typeof card.metadata === "object"
              ? card.metadata
              : payload?.metadata || null,
          loading: false,
          error: "",
        }));
      } catch (err) {
        console.error("Executive contextual explanation error:", err);
        setExecutiveExplain((current) => ({
          ...current,
          action,
          actionIndex,
          question,
          loading: false,
          error:
            String(
              err?.message ||
                err ||
                "Executive contextual explanation is unavailable."
            ),
        }));
      }
    },
    [
      fetchToken,
      framework,
      normalizedBase,
      tenantId,
      userId,
      windowValue,
    ]
  );

  const openExecutiveActionExplain = useCallback(
    (action, actionIndex) => {
      setExecutiveExplain({
        open: true,
        action,
        actionIndex,
        question: EXECUTIVE_EXPLAIN_PROMPTS[0],
        card: null,
        metadata: null,
        loading: true,
        error: "",
      });

      askExecutiveActionExplain(
        action,
        actionIndex,
        EXECUTIVE_EXPLAIN_PROMPTS[0]
      );
    },
    [askExecutiveActionExplain]
  );

  const closeExecutiveActionExplain = useCallback(() => {
    setExecutiveExplain((current) => ({
      ...current,
      open: false,
      loading: false,
      error: "",
    }));
  }, []);

  const handleExecutiveExplainAsk = useCallback(
    (question) => {
      if (!executiveExplain.action) return;
      askExecutiveActionExplain(
        executiveExplain.action,
        executiveExplain.actionIndex,
        question
      );
    },
    [
      askExecutiveActionExplain,
      executiveExplain.action,
      executiveExplain.actionIndex,
    ]
  );

  const openStrategicRisks = useCallback(() => {
    setMode?.("posture", {
      framework,
      window: windowValue,
      section: "risk_queue",
      source: "executive_intelligence",
      sourceMode: "executive",
      returnMode: "executive",
      returnTo: "executive_intelligence",
      routeNavNonce: String(Date.now()),
    });
  }, [framework, setMode, windowValue]);

  const buildIdentityRouteParams = useCallback(
    (section = "investigation_summary") => {
      const finding = executiveIdentitySignal.topFinding;
      if (!finding) return null;

      return {
        framework,
        controlId: finding.controlId || undefined,
        ruleId: finding.ruleId || finding.rule_id || undefined,
        resourceId: finding.resourceId || undefined,
        window: windowValue,
        section,
        source: "executive_intelligence",
        sourceMode: "executive",
        returnMode: "executive",
        returnTo: "executive_intelligence",
        routeNavNonce: String(Date.now()),
      };
    },
    [executiveIdentitySignal.topFinding, framework, windowValue]
  );

  const openIdentityPosture = useCallback(() => {
    const params = buildIdentityRouteParams("investigation_summary");
    if (!params) return;
    setMode?.("posture", params);
  }, [buildIdentityRouteParams, setMode]);

  const openIdentityDrift = useCallback(() => {
    const params = buildIdentityRouteParams("identity");
    if (!params) return;
    setMode?.("drift", params);
  }, [buildIdentityRouteParams, setMode]);

  const openIdentityFixPlan = useCallback(() => {
    const params = buildIdentityRouteParams("investigation_summary");
    if (!params) return;
    setMode?.("posture", {
      ...params,
      openFixPlan: "1",
    });
  }, [buildIdentityRouteParams, setMode]);

  const buildCodeEdgeRouteParams = useCallback(
    (section = "investigation_summary") => {
      const finding = executiveCodeEdgeSignal.topFinding;
      if (!finding) return null;

      return {
        framework,
        controlId: finding.controlId || undefined,
        ruleId: finding.ruleId || finding.rule_id || undefined,
        resourceId: finding.resourceId || undefined,
        window: windowValue,
        section,
        source: "executive_intelligence",
        sourceMode: "executive",
        returnMode: "executive",
        returnTo: "executive_intelligence",
        routeNavNonce: String(Date.now()),
      };
    },
    [executiveCodeEdgeSignal.topFinding, framework, windowValue]
  );

  const openCodeEdgePosture = useCallback(() => {
    const params = buildCodeEdgeRouteParams("investigation_summary");
    if (!params) return;
    setMode?.("posture", params);
  }, [buildCodeEdgeRouteParams, setMode]);

  const openCodeEdgeDrift = useCallback(() => {
    const params = buildCodeEdgeRouteParams("code_edge");
    if (!params) return;
    setMode?.("drift", params);
  }, [buildCodeEdgeRouteParams, setMode]);

  const openCodeEdgeFixPlan = useCallback(() => {
    const params = buildCodeEdgeRouteParams("investigation_summary");
    if (!params) return;
    setMode?.("posture", {
      ...params,
      openFixPlan: "1",
    });
  }, [buildCodeEdgeRouteParams, setMode]);


  const openWorkflowCenter = useCallback(() => {
    setMode?.("workflows", {
      section: "executive_workflow_signal",
      source: "executive_intelligence",
      sourceMode: "executive",
      returnMode: "executive",
      returnTo: "executive_intelligence",
      routeNavNonce: String(Date.now()),
    });
  }, [setMode]);

  if (loading) {
    return (
      <div className={`oasys-dashboard executive-intelligence-page ${themeMode === "dark" ? "dark" : ""}`}>
        <div className="dash-shell">
          <div className="dash-header">
            <div>
              <div className="dash-title">Executive Intelligence</div>
            </div>
          </div>
          <div className="card" style={{ padding: 20, marginTop: 16 }}>
            Loading Executive Intelligence…
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`oasys-dashboard executive-intelligence-page ${themeMode === "dark" ? "dark" : ""}`}>
        <div className="dash-shell">
          <div className="dash-header">
            <div>
              <div className="dash-title">Executive Intelligence</div>
              <p className="dash-subtitle">Leadership-level compliance analytics and risk movement.</p>
            </div>
          </div>
          <div className="card" style={{ padding: 20, marginTop: 16 }}>
            <div style={{ color: "#ff7b7b" }}>Failed to load Executive Intelligence: {error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`oasys-dashboard executive-intelligence-page ${themeMode === "dark" ? "dark" : ""}`}>
      <div className="dash-shell">
        <header className="executive-intelligence-header">
          <div className="executive-intelligence-header__content">
            <h1>Executive Intelligence</h1>
            <p>
              Leadership visibility into posture movement, remediation effectiveness, SLA health,
              validation outcomes, and strategic risk velocity.
            </p>
          </div>

          <div className="executive-intelligence-header__controls">
            <label className="executive-intelligence-framework-control">
              <span>Framework</span>
              <Select
                aria-label="Executive Intelligence framework"
                value={framework}
                onChange={(event) => setFramework(event.target.value)}
              >
                {appliedFrameworks.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </Select>
            </label>

            <div
              className="executive-intelligence-window-control"
              role="group"
              aria-label="Reporting window"
            >
              {WINDOW_OPTIONS.map((item) => {
                const active = item.value === windowValue;
                return (
                  <button
                    key={item.value}
                    type="button"
                    className={`executive-intelligence-window-option${
                      active ? " is-active" : ""
                    }`}
                    aria-pressed={active}
                    onClick={() => setWindowValue(item.value)}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>

            <ActionGroup className="executive-intelligence-export-actions">
              <Button
                type="button"
                variant="secondary"
                size="small"
                onClick={handleExportSummary}
                disabled={exporting || pdfExporting}
              >
                {exporting ? "Exporting…" : "Export JSON"}
              </Button>
              <Button
                type="button"
                variant="primary"
                size="small"
                onClick={handleDownloadPdfReport}
                disabled={exporting || pdfExporting}
              >
                {pdfExporting ? "Preparing PDF…" : "Download PDF report"}
              </Button>
            </ActionGroup>
          </div>
        </header>

        {(exportNotice || exportError) && (
          <section
            className={`executive-intelligence-export-notice${
              exportError ? " is-error" : " is-success"
            }`}
            role={exportError ? "alert" : "status"}
            aria-live={exportError ? "assertive" : "polite"}
          >
            {exportError || exportNotice}
          </section>
        )}

        <ExecutiveIntelligencePanel
          apiBaseUrl={normalizedBase}
          getAccessToken={fetchToken}
          tenantId={tenantId}
          userId={userId}
          framework={framework}
          windowValue={windowValue}
          themeMode={themeMode}
        />

        <section className="hero-grid">
          <div className="card hero-main">
            <div className="card-header">
              <div>
                <div className="dash-eyebrow">OASYS · EXECUTIVE HEALTH</div>
                <div className="card-title">Organizational Compliance Health</div>
              </div>
              <span className={`risk-pill ${riskMeta.className}`}>{riskMeta.label}</span>
            </div>

            <div className="card-body hero-body">
              <div className="hero-ring">
                <HealthDonut value={latestPosture} />
              </div>

              <div className="hero-metrics">
                <div className="executive-intelligence-summary">
                  <div className="executive-intelligence-summary__eyebrow">
                    Executive Summary
                  </div>
                  {buildExecutiveSummaryCopy(
                    summary?.executive_summary,
                    validationMetrics
                  )}
                </div>

                <div className="hero-kpis">
                  <ExecutiveMetricCard
                    label="Raw Score"
                    value={formatPercent(summary?.raw_score)}
                    footnote={summary?.posture_source || "posture source"}
                    tone={getToneForScore(summary?.raw_score)}
                  />
                  <ExecutiveMetricCard
                    label="Operational Score"
                    value={formatPercent(summary?.operational_score)}
                    footnote="leadership health score"
                    tone={getToneForScore(summary?.operational_score)}
                  />
                  <ExecutiveMetricCard
                    label="Risk Velocity"
                    value={formatSigned(riskVelocity.net, 0)}
                    footnote={`${formatNumber(riskVelocity.new)} new · ${formatNumber(
                      riskVelocity.resolved
                    )} resolved`}
                    tone={safeNumber(riskVelocity.net) <= 0 ? "ok" : "danger"}
                  />
                  <ExecutiveMetricCard
                    label="MTTR"
                    value={formatDays(summary?.mttr_days)}
                    footnote="mean time to remediate"
                    tone="accent"
                  />
                  <ExecutiveMetricCard
                    label="Validation Acceptance"
                    value={
                      validationMetrics.runs > 0
                        ? formatPercent(validationMetrics.acceptanceRate)
                        : "No runs"
                    }
                    footnote={
                      validationMetrics.runs > 0
                        ? `${formatNumber(
                            validationMetrics.acceptedOutcomes
                          )} accepted · ${formatPercent(
                            validationMetrics.failureRate
                          )} failed`
                        : "No validation activity in this window"
                    }
                    tone={
                      validationMetrics.runs === 0
                        ? "default"
                        : validationMetrics.acceptanceRate >= 80
                        ? "ok"
                        : validationMetrics.acceptanceRate >= 50
                        ? "warn"
                        : "danger"
                    }
                  />
                  <ExecutiveMetricCard
                    label="SLA Breach Rate"
                    value={formatPercent(summary?.sla_breach_rate)}
                    footnote={`${formatNumber(sla.breached_tasks)} breached active tasks`}
                    tone={
                      safeNumber(summary?.sla_breach_rate) > 10
                        ? "danger"
                        : safeNumber(summary?.sla_breach_rate) > 0
                        ? "warn"
                        : "ok"
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="hero-side executive-intelligence-hero-side">
            <section className="card executive-framework-movement-card">
              <div className="card-header">
                <div>
                  <div className="card-title">Framework Movement</div>
                  <div className="section-subtitle">
                    Direction across normalized framework snapshots.
                  </div>
                </div>
              </div>
              <div className="card-body">
                <FrameworkMovementSummary
                  rows={frameworkRows}
                  selectedFramework={framework}
                />
              </div>
            </section>

            <section className="card executive-intelligence-actions-card">
              <div className="card-header">
                <div className="card-title">Executive Actions</div>
              </div>
              <div className="card-body">
                <ActionGroup className="executive-intelligence-actions-card__buttons">
                  <Button
                    type="button"
                    variant="secondary"
                    size="small"
                    onClick={openStrategicRisks}
                  >
                    Review strategic risks
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="small"
                    onClick={() => setMode?.("dashboard")}
                  >
                    Open Mission Control
                  </Button>
                </ActionGroup>
                <p className="executive-intelligence-source-note">
                  Grounded in live posture, remediation, validation, governed history,
                  and current connector evidence.
                </p>
              </div>
            </section>
          </div>
        </section>

        <section className="executive-intelligence-metric-grid">
          <ExecutiveMetricCard
            label="Tasks Opened"
            value={formatNumber(throughput.tasks_opened)}
            footnote={`${windowValue} throughput`}
            tone="accent"
          />
          <ExecutiveMetricCard
            label="Tasks Closed"
            value={formatNumber(throughput.tasks_closed)}
            footnote={`${formatPercent(throughput.closure_rate)} closure rate`}
            tone="ok"
          />
          <ExecutiveMetricCard
            label="Validation Runs"
            value={formatNumber(validationMetrics.runs)}
            footnote={`${formatNumber(validationMetrics.acceptedOutcomes)} accepted outcomes`}
            tone="accent"
          />
          <ExecutiveMetricCard
            label="Validation Acceptance"
            value={
              validationMetrics.runs > 0
                ? formatPercent(validationMetrics.acceptanceRate)
                : "No runs"
            }
            footnote={
              validationMetrics.runs > 0
                ? `${formatNumber(validationMetrics.passed)} passed · ${formatNumber(
                    validationMetrics.approvedExceptions
                  )} exceptions`
                : "No validation activity in this window"
            }
            tone={
              validationMetrics.runs === 0
                ? "default"
                : validationMetrics.acceptanceRate >= 80
                ? "ok"
                : validationMetrics.acceptanceRate >= 50
                ? "warn"
                : "danger"
            }
          />
          <ExecutiveMetricCard
            label="SLA Breaches"
            value={formatNumber(sla.breached_tasks)}
            footnote={`${formatNumber(sla.active_tasks)} active tasks`}
            tone={safeNumber(sla.breached_tasks) > 0 ? "warn" : "ok"}
          />
        </section>

        <section
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 16,
            marginTop: 16,
          }}
        >
          {strategicSignals.map((signal) => (
            <InsightCard
              key={`${signal.eyebrow}-${signal.title}`}
              eyebrow={signal.eyebrow}
              title={signal.title}
              detail={signal.detail}
              tone={signal.tone}
            />
          ))}
        </section>

        <div
          id="leadership-action-briefing"
          ref={leadershipActionBriefingRef}
          style={{ scrollMarginTop: 96 }}
        >
          <LeadershipActionBriefing
            actions={leadershipActions}
            onOpenAction={handleLeadershipAction}
            onExplainAction={openExecutiveActionExplain}
          />
        </div>

        <ExecutiveIdentityRiskCard
          signal={executiveIdentitySignal}
          framework={framework}
          onOpenPosture={openIdentityPosture}
          onOpenDrift={openIdentityDrift}
          onOpenFixPlan={openIdentityFixPlan}
        />

        <ExecutiveCodeEdgeRiskCard
          signal={executiveCodeEdgeSignal}
          framework={framework}
          onOpenPosture={openCodeEdgePosture}
          onOpenDrift={openCodeEdgeDrift}
          onOpenFixPlan={openCodeEdgeFixPlan}
        />

        <ExecutiveWorkflowSignalCard
          signal={executiveWorkflowSignal}
          onOpenWorkflowCenter={openWorkflowCenter}
        />

        <section className="dash-grid" style={{ marginTop: 16 }}>
          <aside className="filters-column" aria-label="Executive intelligence side rail">
            <section className="filters-card">
              <div className="filters-header">
                <div className="filters-title">Selected Framework</div>
              </div>
              <div className="hero-open-body">
                {selectedFrameworkHealth ? (
                  <>
                    <div className="open-item-pill" style={{ width: "100%" }}>
                      <div className="open-item-label">Movement</div>
                      <div
                        className="open-item-value"
                        style={getToneStyle(
                          selectedFrameworkHealth.trend === "improving"
                            ? "ok"
                            : selectedFrameworkHealth.trend === "deteriorating"
                            ? "danger"
                            : "warn"
                        )}
                      >
                        {formatSigned(selectedFrameworkHealth.movement)}
                      </div>
                    </div>
                    <div className="open-item-pill" style={{ width: "100%" }}>
                      <div className="open-item-label">Trend</div>
                      <div className="open-item-value" style={{ textTransform: "capitalize" }}>
                        {selectedFrameworkHealth.trend || "stable"}
                      </div>
                    </div>
                    <div style={{ color: "var(--muted)", fontSize: 12, lineHeight: 1.5 }}>
                      Latest snapshot: {formatShortDateTime(selectedFrameworkHealth.latest_at)}
                    </div>
                  </>
                ) : (
                  <div className="muted">No selected framework trend available.</div>
                )}
              </div>
            </section>

            <section className="filters-card">
              <div className="filters-header">
                <div className="filters-title">Analytics Scope</div>
              </div>
              <div style={{ display: "grid", gap: 10, color: "var(--muted)", fontSize: 12 }}>
                <div>
                  Framework: <strong style={{ color: "var(--text)" }}>{framework}</strong>
                </div>
                <div>
                  Window: <strong style={{ color: "var(--text)" }}>{windowValue}</strong>
                </div>
                <div>
                  Trend source:{" "}
                  <strong style={{ color: "var(--text)" }}>
                    {formatSourceLabel(trends?.snapshot_source)}
                  </strong>
                </div>
              </div>
            </section>
          </aside>

          <div className="content-column">
            <SimpleLineChart
              title="Posture Trend"
              subtitle="Historical posture snapshots for the selected framework."
              rows={postureTrend}
              valueAccessor={(row) => row.operational_score ?? row.latest_score ?? row.raw_score}
              labelAccessor={(row) => formatDate(row.captured_at)}
              tone={trendSummary.tone === "danger" ? "danger" : "ok"}
              windowValue={windowValue}
            />

            <SegmentedValidationChart
              rows={validationTrend}
              windowValue={windowValue}
            />

            <ThroughputChart
              rows={throughputTrend}
              windowValue={windowValue}
            />
          </div>
        </section>

        <ContextualExplainDrawer
          open={executiveExplain.open}
          record={executiveExplain.action}
          recordLabel={
            executiveExplain.action?.title || "Executive leadership action"
          }
          question={executiveExplain.question}
          card={executiveExplain.card}
          metadata={executiveExplain.metadata}
          loading={executiveExplain.loading}
          error={executiveExplain.error}
          themeMode={themeMode}
          prompts={EXECUTIVE_EXPLAIN_PROMPTS}
          contextDescription="Grounded reasoning for this exact canonical Executive leadership action. Executive Analytics remains authoritative for action ordering and deterministic OASYS state remains authoritative."
          onAsk={handleExecutiveExplainAsk}
          onClose={closeExecutiveActionExplain}
        />

        <div className="dash-footnote">
          Executive Intelligence briefing consolidates grounded data from /executive/summary,
          /executive/remediation-analytics, /executive/framework-health, /executive/trends,
          normalized findings, and /workflows/summary.
        </div>
      </div>
    </div>
  );
}
